import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData, ConvertToGoodReceipt, ConvertPurchaseVerify } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Add, ShoppingCart, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, ExpandMore, ExpandLess, CheckCircleOutline, Assignment, Visibility, Edit, Delete } from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Collapse, Divider, Grid,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery
} from '@mui/material';
import PurchaseModal from './PurchaseModal';
import VerifyPurchaseModal from './VerifyPurchaseModal';
import PaymentReceiptsModal from '../PaymentReceipts/PaymentReceiptsModal';
import Pagination from '../../common/Pagination';

const PurchaseIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();
  const { logAudit } = useAuditLog();

  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Verify modal states
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyPurchaseData, setVerifyPurchaseData] = useState(null);
  const [verifyPurchaseDetails, setVerifyPurchaseDetails] = useState([]);

  // Data states
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);

  // Inactive modal states
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [inactiveViewData, setInactiveViewData] = useState(null);
  const [inactiveViewDetails, setInactiveViewDetails] = useState([]);

  // Converted PO List modal states
  const [convertedPOModalOpen, setConvertedPOModalOpen] = useState(false);
  const [convertedPOList, setConvertedPOList] = useState([]);
  const [requestCount, setRequestCount] = useState(0);

  // PO to Purchase Create modal states
  const [poToPurchaseModalOpen, setPOToPurchaseModalOpen] = useState(false);
  const [selectedPOData, setSelectedPOData] = useState(null);
  const [selectedPODetails, setSelectedPODetails] = useState([]);
  const [poToPurchaseFormData, setPOToPurchaseFormData] = useState({});

  // PO to Purchase - Get Items modal states
  const [poGetItemsModalOpen, setPOGetItemsModalOpen] = useState(false);
  const [poGetItemsSearch, setPOGetItemsSearch] = useState('');
  const [poSelectedItems, setPOSelectedItems] = useState([]);

  // PO Details View modal states
  const [poDetailsModalOpen, setPODetailsModalOpen] = useState(false);
  const [poDetailsData, setPODetailsData] = useState(null);
  const [poDetailsItems, setPODetailsItems] = useState([]);

  // Purchase Return modal states
  const [purchaseReturnModalOpen, setPurchaseReturnModalOpen] = useState(false);
  const [purchaseReturnData, setPurchaseReturnData] = useState(null);
  const [purchaseReturnDetails, setPurchaseReturnDetails] = useState([]);
  const [purchaseReturnFormData, setPurchaseReturnFormData] = useState({});

  // Purchase Details View modal states (for bill number click in Purchase Return)
  const [purchaseDetailsViewModalOpen, setPurchaseDetailsViewModalOpen] = useState(false);
  const [purchaseDetailsViewData, setPurchaseDetailsViewData] = useState(null);
  const [purchaseDetailsViewItems, setPurchaseDetailsViewItems] = useState([]);

  // Supplier drill-down modal states
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPurchases, setSupplierPurchases] = useState([]);
  const [expandedPurchaseId, setExpandedPurchaseId] = useState(null);

  // Purchase drill-down states for animated expansion
  const [expandedPurchaseRowId, setExpandedPurchaseRowId] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [purchaseDetails, setPurchaseDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Payment Status popup
  const [paymentPopupAnchor, setPaymentPopupAnchor] = useState(null);
  const [paymentPopupData, setPaymentPopupData] = useState(null);

  // Payment View Modal
  const [paymentViewModal, setPaymentViewModal] = useState({ open: false });
  const [paymentViewFormData, setPaymentViewFormData] = useState({});
  const [paymentViewDetails, setPaymentViewDetails] = useState([]);
  const [paymentViewLoading, setPaymentViewLoading] = useState(false);

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Convert to Good Receipt confirmation modal
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertItemId, setConvertItemId] = useState(null);
  const [convertItemNo, setConvertItemNo] = useState('');

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Watch for changes in selectedPODetails and recalculate totals
  useEffect(() => {
    if (selectedPODetails.length > 0) {
      calculatePOTotals(selectedPODetails);
    }
  }, [selectedPODetails.length]); // Only trigger when items are added/removed

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Watch for GST calculation type changes in Purchase Return and recalculate all details
  useEffect(() => {
    if (purchaseReturnDetails.length > 0 && purchaseReturnFormData.GstCalculationType) {
      // Recalculate all return details when GST calculation type changes
      const updatedDetails = purchaseReturnDetails.map(detail => {
        const returnQty = parseFloat(detail.ReturnQuantity) || 0;
        const rate = parseFloat(detail.Rate) || 0;
        const gstRate = parseFloat(detail.GSTRate) || 0;
        const discountPct = parseFloat(detail.DiscountPercentage) || 0;

        if (returnQty === 0) return detail; // Skip items with no return quantity

        let returnAmount, returnDiscountAmount, returnTaxableAmount, returnGSTAmount;

        if (isIncludingTaxForReturn()) {
          // Rate includes GST - calculate backwards
          const totalWithGst = returnQty * rate;
          const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
          returnDiscountAmount = (baseAmountBeforeGST * discountPct) / 100;
          returnTaxableAmount = baseAmountBeforeGST - returnDiscountAmount;
          returnGSTAmount = (returnTaxableAmount * gstRate) / 100;
          returnAmount = baseAmountBeforeGST; // Amount before GST and discount
        } else {
          // Rate excludes GST - normal calculation
          returnAmount = returnQty * rate;
          returnDiscountAmount = (returnAmount * discountPct) / 100;
          returnTaxableAmount = returnAmount - returnDiscountAmount;
          returnGSTAmount = (returnTaxableAmount * gstRate) / 100;
        }

        return {
          ...detail,
          ReturnAmount: Math.round(returnAmount * 100) / 100,
          ReturnDiscountAmount: Math.round(returnDiscountAmount * 100) / 100,
          ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
          ReturnCGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
          ReturnSGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
          ReturnIGSTAmount: 0,
          ReturnTotalAmount: Math.round((returnTaxableAmount + returnGSTAmount) * 100) / 100
        };
      });

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }
  }, [purchaseReturnFormData.GstCalculationType]); // Trigger when GST calculation type changes

  // Fetch functions
  const fetchPurchases = async () => {
    try {
      const activeResponse = await fetchData('PurchaseApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));
      fetchConvertedPOList().then(poList => setRequestCount(poList.length))

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PurchaseApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive purchases:', inactiveError);
      }

      setPurchases([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    }
  };

  const fetchSuppliers = async () => {

    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      setSuppliers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      setBranches(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || [];
      let freightData = null, packingData = null;
      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing'));
      }
      setFreightPackingGST({ freight: freightData, packing: packingData });
      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };

  const fetchPurchaseReturnMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching purchase return max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching purchases...');
    try {
      await Promise.allSettled([
        fetchPurchases(),
        fetchSuppliers(),
        fetchItems(),
        fetchBranches(),
        fetchFreightAndPackingGST(),
        // Load request count on initial load
        fetchConvertedPOList().then(poList => setRequestCount(poList.length))
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Purchase data specifically for Purchase Return
  const fetchPurchaseForReturn = async (purchaseId) => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/PurchaseData/${companyId}/${purchaseId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching purchase data for return:', error);
      return null;
    }
  };

  // Fetch single Purchase details for view modal
  const fetchSinglePurchase = async (purchaseId) => {
    try {
      const response = await fetchData('PurchaseApi', `/${purchaseId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single purchase:', error);
      return null;
    }
  };

  // Fetch inactive Purchase details for view modal
  const fetchInactivePurchaseDetails = async (id) => {
    try {
      const response = await fetchData('PurchaseApi', `/InActivePurchaseListDetails/${id}/${branchId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching inactive purchase details:', error);
      return null;
    }
  };

  // Fetch Converted PO List
  const fetchConvertedPOList = async () => {
    try {
      const response = await fetchData('PurchaseApi', `/PurchaseOrderToPurchase/${companyId}/${branchId}`, token);
      return response.response_data || response || [];
    } catch (error) {
      console.error('Error fetching converted PO list:', error);
      return [];
    }
  };

  // Handle Request button click - Open Converted PO List modal
  const handleOpenConvertedPOModal = async () => {
    setModalLoading(true);
    const poList = await fetchConvertedPOList();
    setConvertedPOList(poList);
    setRequestCount(poList.length);
    setModalLoading(false);
    logAudit(AUDIT_ACTION.VIEW, 'PO Request', 0);
    setConvertedPOModalOpen(true);
  };

  const handleCloseConvertedPOModal = () => {
    setConvertedPOModalOpen(false);
    setConvertedPOList([]);
  };

  // Fetch PO details for PO to Purchase
  const fetchPODetailsForPurchase = async (poId) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/${poId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching PO details:', error);
      return null;
    }
  };

  // Handle "To Purchase" button click
  const handleOpenPOToPurchaseModal = async (po) => {
    setModalLoading(true);
    logAudit(AUDIT_ACTION.VIEW, 'To Purchase', po.Id || 0);
    const poDetails = await fetchPODetailsForPurchase(po.Id);

    if (poDetails) {
      const poData = poDetails.PurchaseOrder || poDetails;
      const details = poDetails.PurchaseOrderDetails || [];

      setSelectedPOData(poData);

      // Initialize details with proper calculation structure
      const initializedDetails = details.map(d => {
        // Check if this is an IGST transaction
        const isIGSTTransaction = poData?.InvoiceTypeName?.toLowerCase().includes('igst');

        // If it's IGST, convert CGST+SGST to IGST
        let cgstAmount = d.CGSTAmount || 0;
        let sgstAmount = d.SGSTAmount || 0;
        let igstAmount = d.IGSTAmount || 0;

        if (isIGSTTransaction && (cgstAmount > 0 || sgstAmount > 0)) {
          // Convert CGST + SGST to IGST
          igstAmount = cgstAmount + sgstAmount;
          cgstAmount = 0;
          sgstAmount = 0;
        }

        return {
          ...d,
          ReceivedQuantity: d.Quantity || 0,
          DamageQuantity: 0,
          StockQuantity: d.Quantity || 0,
          PendingQuantity: d.PendingQuantity || d.Quantity || 0,
          Amount: (d.Quantity || 0) * (d.Rate || 0),
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: (d.TaxableAmount != null && d.TaxableAmount !== 0) ? d.TaxableAmount : ((d.Quantity || 0) * (d.Rate || 0) - (d.DiscountAmount || 0)),
          CGSTAmount: cgstAmount,
          SGSTAmount: sgstAmount,
          IGSTAmount: igstAmount,
          TotalAmount: d.TotalAmount || ((d.Quantity || 0) * (d.Rate || 0) - (d.DiscountAmount || 0) + cgstAmount + sgstAmount + igstAmount)
        };
      });

      setSelectedPODetails(initializedDetails);

      // Get GST rates for freight and packing
      const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
      const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
      const maxItemGSTRate = initializedDetails.length > 0 ? Math.max(...initializedDetails.map(d => parseFloat(d.GSTRate) || 0)) : 0;

      const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
      const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

      // Calculate initial freight and packing GST amounts
      const freightAmount = poData.FreightAmount || 0;
      const packingAmount = poData.PackingAmount || 0;
      const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
      const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

      // Calculate totals from items (after IGST conversion)
      const itemsTotal = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0);
      const itemsAmount = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
      const itemsDiscount = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.DiscountAmount) || 0), 0);
      const itemsTaxable = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0);
      const itemsCGST = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.CGSTAmount) || 0), 0);
      const itemsSGST = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.SGSTAmount) || 0), 0);
      const itemsIGST = initializedDetails.reduce((sum, d) => sum + (parseFloat(d.IGSTAmount) || 0), 0);

      // Calculate grand total
      const tcsAmount = poData.TCSAmount || 0;
      const grandTotal = itemsTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
      const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
      const finalTotal = Math.round(grandTotal);

      const initialFormData = {
        PurchaseOrderId: poData.Id || poData.PurchaseOrderId,
        PurchaseOrderNo: poData.PurchaseOrderNo || poData.BillNumber || '',
        PurchaseOrderDate: poData.PurchaseOrderDate?.split('T')[0] || poData.BillDate?.split('T')[0] || '',
        MasterId: poData.MasterId,
        MasterName: poData.MasterName || '',
        InvoiceTypeId: poData.InvoiceTypeId || 0,
        InvoiceTypeName: poData.InvoiceTypeName || '',
        GSTNumber: poData.GSTNumber || suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId)?.GSTNumber || suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId)?.GSTNo || '',
        BillNumber: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        GstCalculationType: poData.GstCalculationType || 1,
        GstCalculationTypeName: poData.GstCalculationTypeName || 'Excluding Tax',
        Amount: Math.round(itemsAmount * 100) / 100,
        DiscountAmount: Math.round(itemsDiscount * 100) / 100,
        TaxableAmount: Math.round(itemsTaxable * 100) / 100,
        CGSTAmount: Math.round(itemsCGST * 100) / 100,
        SGSTAmount: Math.round(itemsSGST * 100) / 100,
        IGSTAmount: Math.round(itemsIGST * 100) / 100,
        FreightAmount: freightAmount,
        FreightGSTRate: effectiveFreightGSTRate,
        FreightGSTAmount: Math.round(freightGSTAmount * 100) / 100,
        PackingAmount: packingAmount,
        PackingGSTRate: effectivePackingGSTRate,
        PackingGSTAmount: Math.round(packingGSTAmount * 100) / 100,
        TCSAmount: tcsAmount,
        RoundOff: Math.round(roundOff * 100) / 100,
        TotalAmount: finalTotal,
        PaidAmount: 0,
        BalanceAmount: finalTotal,
        Remarks: '',
        AdditionalRemarks: ''
      };

      setPOToPurchaseFormData(initialFormData);
      // Pass initialFormData directly to avoid stale closure
      setTimeout(() => calculatePOTotals(initializedDetails, initialFormData), 0);
    }

    setModalLoading(false);
    setConvertedPOModalOpen(false);
    setPOToPurchaseModalOpen(true);
  };

  const handleClosePOToPurchaseModal = () => {
    setPOToPurchaseModalOpen(false);
    setSelectedPOData(null);
    setSelectedPODetails([]);
    setPOToPurchaseFormData({});
  };

  // Handle PO Details View Modal
  const handleOpenPODetailsModal = async (poData) => {
    setModalLoading(true);

    try {
      const poDetails = await fetchPODetailsForPurchase(poData.Id);

      if (poDetails) {
        const poInfo = poDetails.PurchaseOrder || poDetails;
        const details = poDetails.PurchaseOrderDetails || [];

        setPODetailsData(poInfo);
        setPODetailsItems(details);
      } else {
        setPODetailsData(poData);
        setPODetailsItems([]);
      }
    } catch (error) {
      console.error('Error fetching PO details:', error);
      setPODetailsData(poData);
      setPODetailsItems([]);
    }

    setModalLoading(false);
    setPODetailsModalOpen(true);
  };

  const handleClosePODetailsModal = () => {
    setPODetailsModalOpen(false);
    setPODetailsData(null);
    setPODetailsItems([]);
  };

  // Handle Purchase Return Modal
  const handleOpenPurchaseReturnModal = async (purchaseItem) => {
    setModalLoading(true);

    try {
      console.log('Opening Purchase Return Modal for:', purchaseItem);
      const purchaseReturnData = await fetchPurchaseForReturn(purchaseItem.Id);
      console.log('Fetched purchase return data:', purchaseReturnData);

      if (purchaseReturnData) {
        const purchaseData = purchaseReturnData.PurchaseReturn || purchaseReturnData;
        const details = purchaseReturnData.PurchaseReturnDetails || purchaseReturnData.PurchaseDetails || [];

        console.log('Purchase data:', purchaseData);
        console.log('Purchase data keys:', Object.keys(purchaseData));
        console.log('BillNumber:', purchaseData.BillNumber);
        console.log('PurchaseNo:', purchaseData.PurchaseNo);
        console.log('Purchase return details array:', details);
        console.log('Details length:', details.length);

        setPurchaseReturnData(purchaseData);

        // Initialize return details with return quantities set to 0
        const initializedReturnDetails = details.map(d => ({
          ...d,
          ReturnQuantity: d.Quantity || 0, // Use existing return quantity if available
          ReturnAmount: d.Amount || 0,
          ReturnDiscountAmount: d.DiscountAmount || 0,
          ReturnTaxableAmount: d.TaxableAmount || 0,
          ReturnCGSTAmount: d.CGSTAmount || 0,
          ReturnSGSTAmount: d.SGSTAmount || 0,
          ReturnIGSTAmount: d.IGSTAmount || 0,
          ReturnTotalAmount: d.TotalAmount || 0,
          MaxReturnQuantity: d.PurchaseQuantity || 0, // Use PurchaseQuantity as max returnable quantity
          // Keep original fields for reference
          OriginalQuantity: d.Quantity || 0,
          OriginalAmount: d.Amount || 0,
          OriginalTaxableAmount: d.TaxableAmount || 0,
          OriginalCGSTAmount: d.CGSTAmount || 0,
          OriginalSGSTAmount: d.SGSTAmount || 0,
          OriginalIGSTAmount: d.IGSTAmount || 0,
          OriginalTotalAmount: d.TotalAmount || 0
        }));

        console.log('Final initialized return details:', initializedReturnDetails);
        setPurchaseReturnDetails(initializedReturnDetails);

        // Fetch max bill number for purchase return
        const maxBillData = await fetchPurchaseReturnMaxBillNo();
        const returnNumber = maxBillData.billPrefix && maxBillData.finYear ?
          `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}` :
          maxBillData.billNumber.toString();

        // Initialize form data for return
        // Calculate GST rates for freight and packing
        const defaultGSTRate = 18; // Default GST rate
        const purchaseDetails = details || [];
        const maxItemGSTRate = purchaseDetails.length > 0 ? Math.max(...purchaseDetails.map(d => parseFloat(d.GSTRate) || 0)) : 0;

        // Use highest GST rate if > 18%, otherwise use default 18%
        const effectiveGSTRate = maxItemGSTRate > defaultGSTRate ? maxItemGSTRate : defaultGSTRate;

        console.log('GST Rate Calculation:', {
          defaultGSTRate,
          maxItemGSTRate,
          effectiveGSTRate,
          purchaseDetails: purchaseDetails.map(d => ({ ItemName: d.ItemName, GSTRate: d.GSTRate }))
        });

        setPurchaseReturnFormData({
          PurchaseId: purchaseData.PurchaseId || purchaseData.Id,
          PurchaseNo: purchaseData.PurchaseNo || purchaseData.BillNumber || '', // Try both fields
          PurchaseBillNumber: purchaseData.BillNumber || purchaseData.PurchaseNo || '', // Keep for display
          PurchaseBillDate: purchaseData.PurchaseDate || purchaseData.BillDate,
          ReturnDate: new Date().toISOString().split('T')[0],
          ReturnNumber: returnNumber, // Auto-generated return number
          MasterId: purchaseData.MasterId,
          MasterName: purchaseData.MasterName,
          InvoiceTypeId: purchaseData.InvoiceTypeId || 0,
          GSTNumber: purchaseData.GSTNumber || '',
          BranchId: purchaseData.BranchId || branchId,
          GstCalculationType: purchaseData.GstCalculationType || 1, // 1 = Excluding Tax, 2 = Including Tax
          GstCalculationTypeName: purchaseData.GstCalculationTypeName || 'Excluding Tax',
          Amount: 0,
          DiscountAmount: 0,
          TaxableAmount: 0,
          CGSTAmount: 0,
          SGSTAmount: 0,
          IGSTAmount: 0,
          FreightAmount: 0,
          FreightGSTRate: effectiveGSTRate,
          FreightGSTAmount: 0,
          PackingAmount: 0,
          PackingGSTRate: effectiveGSTRate,
          PackingGSTAmount: 0,
          TCSAmount: 0,
          RoundOff: 0,
          TotalAmount: 0,
          PaidAmount: 0,
          BalanceAmount: 0,
          Remarks: ''
        });
      } else {
        setPurchaseReturnData(purchaseItem);
        setPurchaseReturnDetails([]);
        setPurchaseReturnFormData({});
      }
    } catch (error) {
      console.error('Error fetching purchase details for return:', error);
      setPurchaseReturnData(purchaseItem);
      setPurchaseReturnDetails([]);
      setPurchaseReturnFormData({});
    }

    setModalLoading(false);
    setPurchaseReturnModalOpen(true);
  };

  const handleClosePurchaseReturnModal = () => {
    setPurchaseReturnModalOpen(false);
    setPurchaseReturnData(null);
    setPurchaseReturnDetails([]);
    setPurchaseReturnFormData({});
  };

  // Handle Purchase Details View Modal (for bill number click)
  const handleOpenPurchaseDetailsViewModal = async (purchaseData) => {
    setModalLoading(true);

    try {
      // If called from Purchase Return modal, use PurchaseId instead of Id
      const purchaseId = purchaseData.PurchaseId || purchaseData.Id;
      console.log('Opening Purchase Details View for Purchase ID:', purchaseId);
      console.log('Purchase Data:', purchaseData);

      const purchaseDetails = await fetchSinglePurchase(purchaseId);

      if (purchaseDetails) {
        const purchaseInfo = purchaseDetails.Purchase || purchaseDetails;
        const details = purchaseDetails.PurchaseDetails || [];

        console.log('Fetched Purchase Info:', purchaseInfo);
        console.log('Fetched Purchase Details:', details);

        setPurchaseDetailsViewData(purchaseInfo);
        setPurchaseDetailsViewItems(details);
      } else {
        setPurchaseDetailsViewData(purchaseData);
        setPurchaseDetailsViewItems([]);
      }
    } catch (error) {
      console.error('Error fetching purchase details:', error);
      setPurchaseDetailsViewData(purchaseData);
      setPurchaseDetailsViewItems([]);
    }

    setModalLoading(false);
    setPurchaseDetailsViewModalOpen(true);
  };

  const handleClosePurchaseDetailsViewModal = () => {
    setPurchaseDetailsViewModalOpen(false);
    setPurchaseDetailsViewData(null);
    setPurchaseDetailsViewItems([]);
  };

  // Helper function to check if current GST calculation type is "Including Tax"
  const isIncludingTaxForReturn = () => {
    return purchaseReturnFormData?.GstCalculationType === 2 ||
      purchaseReturnFormData?.GstCalculationTypeName === 'Including Tax';
  };

  // Helper function to calculate maximum discount amount for Purchase Return
  const getMaxDiscountAmountForReturn = (quantity, rate, gstRate) => {
    const qty = parseFloat(quantity) || 0;
    const rt = parseFloat(rate) || 0;
    const gst = parseFloat(gstRate) || 0;

    if (isIncludingTaxForReturn()) {
      // For inclusive tax, discount is applied on base amount before GST
      const totalWithGst = qty * rt;
      return totalWithGst / (1 + gst / 100);
    } else {
      // For exclusive tax, discount is applied on amount
      return qty * rt;
    }
  };

  // Handle Purchase Return detail changes
  const handlePurchaseReturnDetailChange = (index, field, value) => {
    const updatedDetails = [...purchaseReturnDetails];

    // Handle non-numeric fields first
    if (!['ReturnQuantity', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      setPurchaseReturnDetails(updatedDetails);
      return;
    }

    // Enhanced numeric validation for discount fields
    if (['DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setPurchaseReturnDetails(updatedDetails);
        calculateReturnTotals(updatedDetails);
        return;
      }

      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;

      // Validate discount percentage not above 100%
      if (field === 'DiscountPercentage') {
        if (numValue > 100) return;
      }

      // Validate discount amount doesn't exceed maximum allowed (100% of item amount)
      if (field === 'DiscountAmount') {
        const currentDetail = updatedDetails[index];
        const returnQty = Math.max(0, parseFloat(currentDetail.ReturnQuantity) || 0);
        const rate = Math.max(0, parseFloat(currentDetail.Rate) || 0);
        const gstRate = Math.max(0, parseFloat(currentDetail.GSTRate) || 0);

        const maxDiscountAmount = getMaxDiscountAmountForReturn(returnQty, rate, gstRate);
        if (numValue > maxDiscountAmount) {
          // Block the input if it exceeds 100% of applicable amount
          return;
        }
      }
    }

    const detail = updatedDetails[index];

    // Handle Return Quantity change
    if (field === 'ReturnQuantity') {
      // Allow empty value
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: 0 };
        calculateReturnTotals(updatedDetails);
        return;
      }

      // Strict numeric validation
      const validNumberRegex = /^[0-9]*\.?[0-9]*$/;
      if (!validNumberRegex.test(value)) {
        return;
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        return;
      }

      // Check if return quantity doesn't exceed purchased quantity
      const maxReturnQty = updatedDetails[index].MaxReturnQuantity || 0;
      if (numValue > maxReturnQty) {
        return;
      }

      const returnQty = numValue;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;
      const discountPct = parseFloat(detail.DiscountPercentage) || 0;

      let returnAmount, returnDiscountAmount, returnTaxableAmount, returnGSTAmount;

      if (isIncludingTaxForReturn()) {
        // Rate includes GST - calculate backwards
        const totalWithGst = returnQty * rate;
        const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
        returnDiscountAmount = (baseAmountBeforeGST * discountPct) / 100;
        returnTaxableAmount = baseAmountBeforeGST - returnDiscountAmount;
        returnGSTAmount = (returnTaxableAmount * gstRate) / 100;
        returnAmount = baseAmountBeforeGST; // Amount before GST and discount
      } else {
        // Rate excludes GST - normal calculation
        returnAmount = returnQty * rate;
        returnDiscountAmount = (returnAmount * discountPct) / 100;
        returnTaxableAmount = returnAmount - returnDiscountAmount;
        returnGSTAmount = (returnTaxableAmount * gstRate) / 100;
      }

      updatedDetails[index] = {
        ...updatedDetails[index],
        ReturnQuantity: returnQty,
        ReturnAmount: Math.round(returnAmount * 100) / 100,
        ReturnDiscountAmount: Math.round(returnDiscountAmount * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnCGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnSGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnIGSTAmount: 0,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnGSTAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }

    // Handle Discount Percentage change with bidirectional sync
    else if (field === 'DiscountPercentage') {
      const numValue = parseFloat(value) || 0;
      // Validation already done above

      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;

      let returnAmount, returnDiscountAmount, returnTaxableAmount, returnGSTAmount;

      if (isIncludingTaxForReturn()) {
        // Rate includes GST - calculate backwards
        const totalWithGst = returnQty * rate;
        const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
        returnDiscountAmount = (baseAmountBeforeGST * numValue) / 100;
        returnTaxableAmount = baseAmountBeforeGST - returnDiscountAmount;
        returnGSTAmount = (returnTaxableAmount * gstRate) / 100;
        returnAmount = baseAmountBeforeGST; // Amount before GST and discount
      } else {
        // Rate excludes GST - normal calculation
        returnAmount = returnQty * rate;
        returnDiscountAmount = (returnAmount * numValue) / 100;
        returnTaxableAmount = returnAmount - returnDiscountAmount;
        returnGSTAmount = (returnTaxableAmount * gstRate) / 100;
      }

      updatedDetails[index] = {
        ...updatedDetails[index],
        DiscountPercentage: Math.round(numValue * 100) / 100,
        ReturnDiscountAmount: Math.round(returnDiscountAmount * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnCGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnSGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnIGSTAmount: 0,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnGSTAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }

    // Handle Discount Amount change with bidirectional sync
    else if (field === 'DiscountAmount') {
      const numValue = parseFloat(value) || 0;
      // Validation already done above

      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;

      let returnAmount, discountPct, returnTaxableAmount, returnGSTAmount;

      if (isIncludingTaxForReturn()) {
        // Rate includes GST - calculate backwards
        const totalWithGst = returnQty * rate;
        const baseAmountBeforeGST = totalWithGst / (1 + gstRate / 100);
        returnAmount = baseAmountBeforeGST; // Amount before GST and discount

        // Ensure discount amount doesn't exceed base amount
        const actualDiscountAmount = Math.min(numValue, baseAmountBeforeGST);
        discountPct = baseAmountBeforeGST > 0 ? (actualDiscountAmount / baseAmountBeforeGST) * 100 : 0;
        // Ensure discount percentage doesn't exceed 100%
        discountPct = Math.min(discountPct, 100);

        returnTaxableAmount = baseAmountBeforeGST - actualDiscountAmount;
        returnGSTAmount = (returnTaxableAmount * gstRate) / 100;
      } else {
        // Rate excludes GST - normal calculation
        returnAmount = returnQty * rate;

        // Ensure discount amount doesn't exceed amount
        const actualDiscountAmount = Math.min(numValue, returnAmount);
        discountPct = returnAmount > 0 ? (actualDiscountAmount / returnAmount) * 100 : 0;
        // Ensure discount percentage doesn't exceed 100%
        discountPct = Math.min(discountPct, 100);

        returnTaxableAmount = returnAmount - actualDiscountAmount;
        returnGSTAmount = (returnTaxableAmount * gstRate) / 100;
      }

      updatedDetails[index] = {
        ...updatedDetails[index],
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        ReturnDiscountAmount: Math.round(numValue * 100) / 100,
        ReturnTaxableAmount: Math.round(returnTaxableAmount * 100) / 100,
        ReturnCGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnSGSTAmount: Math.round((returnGSTAmount / 2) * 100) / 100,
        ReturnIGSTAmount: 0,
        ReturnTotalAmount: Math.round((returnTaxableAmount + returnGSTAmount) * 100) / 100
      };

      setPurchaseReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
    }
  };

  // Calculate Purchase Return totals
  const calculateReturnTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.ReturnAmount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.ReturnDiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.ReturnTaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.ReturnCGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.ReturnSGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.ReturnIGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.ReturnTotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    // Calculate additional charges (proportional to return amount vs original amount)
    const originalAmount = parseFloat(purchaseReturnData?.Amount) || 1;
    const returnRatio = totals.Amount / originalAmount;

    const freightAmount = Math.round((parseFloat(purchaseReturnData?.FreightAmount) || 0) * returnRatio * 100) / 100;
    const freightGSTAmount = Math.round((parseFloat(purchaseReturnData?.FreightGSTAmount) || 0) * returnRatio * 100) / 100;
    const packingAmount = Math.round((parseFloat(purchaseReturnData?.PackingAmount) || 0) * returnRatio * 100) / 100;
    const packingGSTAmount = Math.round((parseFloat(purchaseReturnData?.PackingGSTAmount) || 0) * returnRatio * 100) / 100;
    const tcsAmount = Math.round((parseFloat(purchaseReturnData?.TCSAmount) || 0) * returnRatio * 100) / 100;

    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
    const finalTotal = Math.round(grandTotal);

    // Update form data with calculated totals
    setPurchaseReturnFormData(prev => ({
      ...prev,
      Amount: Math.round(totals.Amount * 100) / 100,
      DiscountAmount: Math.round(totals.DiscountAmount * 100) / 100,
      TaxableAmount: Math.round(totals.TaxableAmount * 100) / 100,
      CGSTAmount: Math.round(totals.CGSTAmount * 100) / 100,
      SGSTAmount: Math.round(totals.SGSTAmount * 100) / 100,
      IGSTAmount: Math.round(totals.IGSTAmount * 100) / 100,
      FreightAmount: freightAmount,
      FreightGSTAmount: freightGSTAmount,
      PackingAmount: packingAmount,
      PackingGSTAmount: packingGSTAmount,
      TCSAmount: tcsAmount,
      RoundOff: Math.round(roundOff * 100) / 100,
      TotalAmount: finalTotal
    }));
  };

  // Handle Purchase Return form field changes
  const handlePurchaseReturnFormChange = (field, value) => {
    setPurchaseReturnFormData(prev => {
      const updatedFormData = {
        ...prev,
        [field]: value
      };

      // If freight or packing amount changes, recalculate GST and totals
      if (field === 'FreightAmount' || field === 'PackingAmount') {
        const freightAmount = field === 'FreightAmount' ? parseFloat(value) || 0 : parseFloat(prev.FreightAmount) || 0;
        const packingAmount = field === 'PackingAmount' ? parseFloat(value) || 0 : parseFloat(prev.PackingAmount) || 0;

        // Use stored GST rates from form data
        const freightGSTRate = parseFloat(prev.FreightGSTRate) || 18;
        const packingGSTRate = parseFloat(prev.PackingGSTRate) || 18;

        // Calculate GST amounts
        const freightGSTAmount = (freightAmount * freightGSTRate) / 100;
        const packingGSTAmount = (packingAmount * packingGSTRate) / 100;

        // Update GST amounts
        updatedFormData.FreightGSTAmount = Math.round(freightGSTAmount * 100) / 100;
        updatedFormData.PackingGSTAmount = Math.round(packingGSTAmount * 100) / 100;

        // Recalculate total: items total + freight + freight GST + packing + packing GST + TCS
        const itemsTotal = parseFloat(prev.TaxableAmount) || 0;
        const itemsGSTTotal = (parseFloat(prev.CGSTAmount) || 0) + (parseFloat(prev.SGSTAmount) || 0) + (parseFloat(prev.IGSTAmount) || 0);
        const tcsAmount = parseFloat(prev.TCSAmount) || 0;

        const grandTotal = itemsTotal + itemsGSTTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
        const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
        const finalTotal = Math.round(grandTotal);

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;

        // Recalculate balance
        const paidAmount = parseFloat(prev.PaidAmount) || 0;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      // If TCS amount changes, recalculate totals
      if (field === 'TCSAmount') {
        const tcsAmount = parseFloat(value) || 0;
        updatedFormData.TCSAmount = tcsAmount; // Ensure TCS amount is set

        const itemsTotal = parseFloat(prev.TaxableAmount) || 0;
        const itemsGSTTotal = (parseFloat(prev.CGSTAmount) || 0) + (parseFloat(prev.SGSTAmount) || 0) + (parseFloat(prev.IGSTAmount) || 0);
        const freightAmount = parseFloat(prev.FreightAmount) || 0;
        const freightGSTAmount = parseFloat(prev.FreightGSTAmount) || 0;
        const packingAmount = parseFloat(prev.PackingAmount) || 0;
        const packingGSTAmount = parseFloat(prev.PackingGSTAmount) || 0;

        const grandTotal = itemsTotal + itemsGSTTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
        const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
        const finalTotal = Math.round(grandTotal);

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;

        // Recalculate balance
        const paidAmount = parseFloat(prev.PaidAmount) || 0;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      // If paid amount changes, recalculate balance
      if (field === 'PaidAmount') {
        const paidAmount = parseFloat(value) || 0;
        const totalAmount = parseFloat(prev.TotalAmount) || 0;
        updatedFormData.BalanceAmount = totalAmount - paidAmount;
      }

      return updatedFormData;
    });
  };

  // Save Purchase Return
  const handleSavePurchaseReturn = async () => {
    // Validation
    const validationErrors = [];

    if (!purchaseReturnFormData.ReturnNumber?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!purchaseReturnFormData.ReturnDate) {
      validationErrors.push('Return Date is required');
    }

    // Check if there are items with return quantity
    const itemsWithReturnQty = purchaseReturnDetails.filter(item => (item.ReturnQuantity || 0) > 0);
    if (itemsWithReturnQty.length === 0) {
      validationErrors.push('At least one item must have return quantity greater than 0');
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase return...');

    try {
      const dataToSend = {
        PurchaseReturn: {
          Id: 0,
          CompanyId: companyId,
          BranchId: purchaseReturnFormData.BranchId || branchId,
          PurchaseId: purchaseReturnFormData.PurchaseId,
          PurchaseNo: purchaseReturnFormData.PurchaseBillNumber,
          PurchaseDate: purchaseReturnFormData.PurchaseBillDate,
          BillNumber: purchaseReturnFormData.ReturnNumber,
          BillDate: purchaseReturnFormData.ReturnDate,
          MasterId: purchaseReturnFormData.MasterId,
          MasterName: purchaseReturnFormData.MasterName,
          InvoiceTypeId: purchaseReturnFormData.InvoiceTypeId || 0,
          GSTNumber: purchaseReturnFormData.GSTNumber || '',
          GstCalculationType: parseInt(purchaseReturnFormData.GstCalculationType) || 1,
          GstCalculationTypeName: purchaseReturnFormData.GstCalculationTypeName || 'Excluding Tax',
          Amount: parseFloat(purchaseReturnFormData.Amount) || 0,
          DiscountAmount: parseFloat(purchaseReturnFormData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(purchaseReturnFormData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(purchaseReturnFormData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(purchaseReturnFormData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(purchaseReturnFormData.IGSTAmount) || 0,
          FreightAmount: parseFloat(purchaseReturnFormData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(purchaseReturnFormData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(purchaseReturnFormData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(purchaseReturnFormData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(purchaseReturnFormData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(purchaseReturnFormData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(purchaseReturnFormData.TCSAmount) || 0,
          RoundOff: parseFloat(purchaseReturnFormData.RoundOff) || 0,
          TotalAmount: parseFloat(purchaseReturnFormData.TotalAmount) || 0,
          PaidAmount: parseFloat(purchaseReturnFormData.PaidAmount) || 0,
          BalanceAmount: parseFloat(purchaseReturnFormData.BalanceAmount) || 0,
          Remarks: purchaseReturnFormData.Remarks || '',
          IsActive: true,
          CreatedBy: userId
        },
        PurchaseReturnDetails: itemsWithReturnQty.map(d => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseReturnId: 0, // Will be set by backend
          ItemId: d.ItemId,
          ItemName: d.ItemName || '',
          BatchNumber: d.BatchNumber || '',
          PurchaseId: purchaseReturnFormData.PurchaseId,
          PurchaseQuantity: parseFloat(d.PurchaseQuantity) || 0, // Original purchased quantity
          Quantity: parseFloat(d.ReturnQuantity) || 0, // Return quantity
          ReturnStockQuantity: parseFloat(d.ReturnQuantity) || 0, // Good return quantity
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.ReturnAmount) || 0,
          DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(d.ReturnDiscountAmount) || 0,
          HSNCode: d.HSNCode || '',
          GSTRate: parseFloat(d.GSTRate) || 0,
          TaxableAmount: parseFloat(d.ReturnTaxableAmount) || 0,
          CGSTAmount: parseFloat(d.ReturnCGSTAmount) || 0,
          SGSTAmount: parseFloat(d.ReturnSGSTAmount) || 0,
          IGSTAmount: parseFloat(d.ReturnIGSTAmount) || 0,
          TotalAmount: parseFloat(d.ReturnTotalAmount) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      await postData('PurchaseReturnApi', '', dataToSend, token);
      // Log audit BEFORE redirect so context is still alive
      logAudit(AUDIT_ACTION.CREATE, 'PurchaseReturnApi', 0, null, {
        BillNumber:   purchaseReturnFormData.ReturnNumber  || '',
        SupplierName: purchaseReturnFormData.MasterName    || '',
        PurchaseNo:   purchaseReturnFormData.PurchaseBillNumber || '',
        TotalAmount:  purchaseReturnFormData.TotalAmount   || 0,
        ItemCount:    itemsWithReturnQty.length,
      });
      setSuccess('Purchase return created successfully!');
      handleClosePurchaseReturnModal();
      await fetchPurchases(); // Refresh the purchase list

      // Redirect to Purchase Return page
      setTimeout(() => {
        history.push('/PurchaseReturnAgainstIndex');
      }, 1500);
    } catch (error) {
      console.error('Error creating purchase return:', error);

      // The API sometimes throws even after a successful insert (e.g. redirect / non-JSON response).
      // If the HTTP status is in the 2xx-3xx range or no status (network/parse error), treat as success.
      const status = error?.response?.status;
      if (!status || status < 400) {
        // Likely saved — log audit BEFORE redirect
        logAudit(AUDIT_ACTION.CREATE, 'PurchaseReturnApi', 0, null, {
          BillNumber:   purchaseReturnFormData.ReturnNumber  || '',
          SupplierName: purchaseReturnFormData.MasterName    || '',
          PurchaseNo:   purchaseReturnFormData.PurchaseBillNumber || '',
          TotalAmount:  purchaseReturnFormData.TotalAmount   || 0,
          ItemCount:    itemsWithReturnQty.length,
        });
        setSuccess('Purchase return created successfully!');
        handleClosePurchaseReturnModal();
        await fetchPurchases();
        setTimeout(() => {
          history.push('/PurchaseReturnAgainstIndex');
        }, 1500);
      } else {
        setError('Failed to create purchase return.');
      }
    }

    setLoading(false);
  };

  // Handle PO to Purchase form field change
  const handlePOToPurchaseFormChange = (field, value) => {
    // Update the form data first
    setPOToPurchaseFormData(prev => {
      const updatedFormData = { ...prev, [field]: value };

      // If GST Type changes, recalculate all items
      if (field === 'GstCalculationType') {
        const isIncludingTax = parseInt(value) === 2;

        // Recalculate all item details based on new GST type
        const recalculatedDetails = selectedPODetails.map(detail => {
          const receivedQty = Math.max(0, parseFloat(detail.ReceivedQuantity) || 0);
          const rate = Math.max(0, parseFloat(detail.Rate) || 0);
          const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);

          let taxableAmount, gstAmount, amount;

          if (isIncludingTax) {
            // Rate includes GST - calculate backwards
            const totalWithGst = receivedQty * rate;
            taxableAmount = totalWithGst / (1 + gstRate / 100);
            amount = taxableAmount;

            const discountPct = parseFloat(detail.DiscountPercentage) || 0;
            const discountAmount = (taxableAmount * discountPct) / 100;

            taxableAmount = taxableAmount - discountAmount;
            gstAmount = (taxableAmount * gstRate) / 100;

            return {
              ...detail,
              Amount: Math.round(amount * 100) / 100,
              DiscountAmount: Math.round(discountAmount * 100) / 100,
              TaxableAmount: Math.round(taxableAmount * 100) / 100,
              CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              IGSTAmount: 0,
              TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100
            };
          } else {
            // Rate excludes GST - normal calculation
            amount = receivedQty * rate;
            const discountPct = parseFloat(detail.DiscountPercentage) || 0;
            const discountAmount = (amount * discountPct) / 100;

            taxableAmount = amount - discountAmount;
            gstAmount = (taxableAmount * gstRate) / 100;

            return {
              ...detail,
              Amount: Math.round(amount * 100) / 100,
              DiscountAmount: Math.round(discountAmount * 100) / 100,
              TaxableAmount: Math.round(taxableAmount * 100) / 100,
              CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
              IGSTAmount: 0,
              TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100
            };
          }
        });

        // Update the details
        setSelectedPODetails(recalculatedDetails);

        // Recalculate totals with new details
        setTimeout(() => {
          calculatePOTotals(recalculatedDetails);
        }, 0);
      }

      // If freight or packing amount/rate changes, recalculate immediately
      if (field === 'FreightAmount' || field === 'PackingAmount' || field === 'FreightGSTRate' || field === 'PackingGSTRate') {
        // Use user-entered GST rate if provided, otherwise use API default or max item rate
        const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
        const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
        const maxItemGSTRate = selectedPODetails.length > 0 ? Math.max(...selectedPODetails.map(d => parseFloat(d.GSTRate) || 0)) : 0;

        const freightAmount = field === 'FreightAmount' ? parseFloat(value) || 0 : parseFloat(updatedFormData.FreightAmount) || 0;
        const packingAmount = field === 'PackingAmount' ? parseFloat(value) || 0 : parseFloat(updatedFormData.PackingAmount) || 0;
        const freightGSTRate = field === 'FreightGSTRate' ? parseFloat(value) || 0 : (parseFloat(updatedFormData.FreightGSTRate) || Math.max(defaultFreightGSTRate, maxItemGSTRate));
        const packingGSTRate = field === 'PackingGSTRate' ? parseFloat(value) || 0 : (parseFloat(updatedFormData.PackingGSTRate) || Math.max(defaultPackingGSTRate, maxItemGSTRate));

        const freightGSTAmount = (freightAmount * freightGSTRate) / 100;
        const packingGSTAmount = (packingAmount * packingGSTRate) / 100;

        // Update GST rates and amounts
        updatedFormData.FreightGSTRate = freightGSTRate;
        updatedFormData.FreightGSTAmount = Math.round(freightGSTAmount * 100) / 100;
        updatedFormData.PackingGSTRate = packingGSTRate;
        updatedFormData.PackingGSTAmount = Math.round(packingGSTAmount * 100) / 100;

        // Recalculate total
        const itemsTotal = selectedPODetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0);
        const tcsAmount = parseFloat(updatedFormData.TCSAmount) || 0;
        const grandTotal = itemsTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
        const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
        const finalTotal = Math.round(grandTotal);
        const paidAmount = parseFloat(updatedFormData.PaidAmount) || 0;

        updatedFormData.RoundOff = Math.round(roundOff * 100) / 100;
        updatedFormData.TotalAmount = finalTotal;
        updatedFormData.BalanceAmount = finalTotal - paidAmount;
      }

      return updatedFormData;
    });

    // Recalculate totals for other fields
    if (['TCSAmount', 'PaidAmount'].includes(field)) {
      const newTCS = field === 'TCSAmount' ? parseFloat(value) || 0 : parseFloat(poToPurchaseFormData.TCSAmount) || 0;
      const newPaid = field === 'PaidAmount' ? parseFloat(value) || 0 : parseFloat(poToPurchaseFormData.PaidAmount) || 0;
      setTimeout(() => {
        setPOToPurchaseFormData(prev => {
          const freightAmount = parseFloat(prev.FreightAmount) || 0;
          const packingAmount = parseFloat(prev.PackingAmount) || 0;
          const freightGSTAmount = parseFloat(prev.FreightGSTAmount) || 0;
          const packingGSTAmount = parseFloat(prev.PackingGSTAmount) || 0;
          const itemsTotal = selectedPODetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0);
          const grandTotal = itemsTotal + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + newTCS;
          const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
          const finalTotal = Math.round(grandTotal);
          return {
            ...prev,
            TotalAmount: finalTotal,
            RoundOff: Math.round(roundOff * 100) / 100,
            BalanceAmount: finalTotal - newPaid
          };
        });
      }, 0);
    }
  };

  // Handle PO to Purchase detail change
  const handlePOToPurchaseDetailChange = (index, field, value) => {
    const updatedDetails = [...selectedPODetails];

    // Handle non-numeric fields first
    if (['ItemId', 'ItemName', 'HSNCode'].includes(field)) {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      setSelectedPODetails(updatedDetails);
      // Always recalculate when ItemId changes (item selection)
      if (field === 'ItemId' || field === 'ItemName') {
        calculatePOTotals(updatedDetails);
      }
      return;
    }

    // Enhanced validation for numeric inputs
    if (['ReceivedQuantity', 'Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      // Allow empty value
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setSelectedPODetails(updatedDetails);
        calculatePOTotals(updatedDetails);
        return;
      }

      // Strict numeric validation - only allow numbers and decimal point
      const validNumberRegex = /^[0-9]*\.?[0-9]*$/;
      if (!validNumberRegex.test(value)) {
        // Invalid input - don't update
        return;
      }

      // Check for valid decimal number
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        // Invalid number - don't update
        return;
      }

      // Additional validation for specific fields
      if (['ReceivedQuantity', 'Quantity', 'Rate'].includes(field) && numValue < 0) {
        // Negative values not allowed for these fields
        return;
      }

      if (['DiscountPercentage'].includes(field) && (numValue < 0 || numValue > 100)) {
        // Discount percentage should be between 0-100
        return;
      }

      // Validate discount amount doesn't exceed item amount
      if (field === 'DiscountAmount') {
        const detail = updatedDetails[index];
        const receivedQty = Math.max(0, parseFloat(detail.ReceivedQuantity) || 0);
        const rate = Math.max(0, parseFloat(detail.Rate) || 0);
        const maxAmount = receivedQty * rate;
        if (numValue > maxAmount) return;
      }

      if (['GSTRate'].includes(field) && (numValue < 0 || numValue > 100)) {
        // GST rate should be between 0-100
        return;
      }
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    // Recalculate item totals when relevant fields change
    if (['ReceivedQuantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const receivedQty = Math.max(0, parseFloat(detail.ReceivedQuantity) || 0);
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(detail.GSTRate) || 0);
      const isIncludingTax = (poToPurchaseFormData.GstCalculationType || 1) === 2;

      let taxableAmount, gstAmount, amount;

      if (isIncludingTax) {
        // Rate includes GST - calculate backwards
        const totalWithGst = receivedQty * rate;
        taxableAmount = totalWithGst / (1 + gstRate / 100);
        amount = taxableAmount;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.min(Math.max(0, parseFloat(value) || 0), taxableAmount);
          discountPct = taxableAmount > 0 ? (discountAmount / taxableAmount) * 100 : 0;
          discountPct = Math.min(discountPct, 100);
        } else if (field === 'DiscountPercentage') {
          discountPct = Math.min(Math.max(0, parseFloat(value) || 0), 100);
          discountAmount = (taxableAmount * discountPct) / 100;
        } else {
          discountPct = Math.min(parseFloat(detail.DiscountPercentage) || 0, 100);
          discountAmount = (taxableAmount * discountPct) / 100;
        }

        taxableAmount = taxableAmount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        // Determine if this is an IGST transaction
        const isIGSTTransaction = selectedPOData?.InvoiceTypeName?.toLowerCase().includes('igst') ||
          poToPurchaseFormData?.InvoiceTypeName?.toLowerCase().includes('igst');

        updatedDetails[index] = {
          ...updatedDetails[index],
          Amount: Math.round(amount * 100) / 100,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: isIGSTTransaction ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          SGSTAmount: isIGSTTransaction ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          IGSTAmount: isIGSTTransaction ? Math.round(gstAmount * 100) / 100 : 0,
          TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100,
          StockQuantity: receivedQty,
          DamageQuantity: parseFloat(detail.DamageQuantity) || 0
        };
      } else {
        // Rate excludes GST - normal calculation
        amount = receivedQty * rate;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.min(Math.max(0, parseFloat(value) || 0), amount);
          discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
          discountPct = Math.min(discountPct, 100);
        } else if (field === 'DiscountPercentage') {
          discountPct = Math.min(Math.max(0, parseFloat(value) || 0), 100);
          discountAmount = (amount * discountPct) / 100;
        } else {
          discountPct = Math.min(parseFloat(detail.DiscountPercentage) || 0, 100);
          discountAmount = (amount * discountPct) / 100;
        }

        taxableAmount = amount - discountAmount;
        gstAmount = (taxableAmount * gstRate) / 100;

        // Determine if this is an IGST transaction
        const isIGSTTransaction = selectedPOData?.InvoiceTypeName?.toLowerCase().includes('igst') ||
          poToPurchaseFormData?.InvoiceTypeName?.toLowerCase().includes('igst');

        updatedDetails[index] = {
          ...updatedDetails[index],
          Amount: Math.round(amount * 100) / 100,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: isIGSTTransaction ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          SGSTAmount: isIGSTTransaction ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          IGSTAmount: isIGSTTransaction ? Math.round(gstAmount * 100) / 100 : 0,
          TotalAmount: Math.round((taxableAmount + gstAmount) * 100) / 100,
          StockQuantity: receivedQty,
          DamageQuantity: parseFloat(detail.DamageQuantity) || 0
        };
      }
    }

    setSelectedPODetails(updatedDetails);
    calculatePOTotals(updatedDetails);
  };

  // Calculate PO to Purchase totals
  const calculatePOTotals = (details, overrideFormData = null) => {
    const currentFormData = overrideFormData || poToPurchaseFormData;

    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    console.log('Calculated item totals:', totals);

    // Get current freight and packing amounts from form data
    const freightAmount = parseFloat(currentFormData.FreightAmount) || 0;
    const packingAmount = parseFloat(currentFormData.PackingAmount) || 0;

    // Get GST rates - use default rates from freight/packing GST data or highest item GST rate
    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate) || 0;
    const maxItemGSTRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0)) : 0;

    const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

    // Calculate GST amounts
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

    const tcsAmount = parseFloat(currentFormData.TCSAmount) || 0;

    // Calculate grand total
    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
    const finalTotal = Math.round(grandTotal);

    const paidAmount = parseFloat(currentFormData.PaidAmount) || 0;
    const balanceAmount = finalTotal - paidAmount;

    // Update form data with calculated totals
    setPOToPurchaseFormData(prev => {
      // Preserve user-entered or saved GST rates; only use effective rate as fallback
      const resolvedFreightGSTRate = parseFloat(prev.FreightGSTRate) || effectiveFreightGSTRate;
      const resolvedPackingGSTRate = parseFloat(prev.PackingGSTRate) || effectivePackingGSTRate;
      const resolvedFreightGSTAmount = (freightAmount * resolvedFreightGSTRate) / 100;
      const resolvedPackingGSTAmount = (packingAmount * resolvedPackingGSTRate) / 100;
      const resolvedGrandTotal = totals.TotalAmount + freightAmount + resolvedFreightGSTAmount + packingAmount + resolvedPackingGSTAmount + tcsAmount;
      const resolvedRoundOff = parseFloat((Math.round(resolvedGrandTotal) - resolvedGrandTotal).toFixed(2));
      const resolvedFinalTotal = Math.round(resolvedGrandTotal);
      const resolvedBalance = resolvedFinalTotal - paidAmount;

      const updatedFormData = {
        ...prev,
        Amount: Math.round(totals.Amount * 100) / 100,
        DiscountAmount: Math.round(totals.DiscountAmount * 100) / 100,
        TaxableAmount: Math.round(totals.TaxableAmount * 100) / 100,
        CGSTAmount: Math.round(totals.CGSTAmount * 100) / 100,
        SGSTAmount: Math.round(totals.SGSTAmount * 100) / 100,
        IGSTAmount: Math.round(totals.IGSTAmount * 100) / 100,
        FreightGSTRate: resolvedFreightGSTRate,
        FreightGSTAmount: Math.round(resolvedFreightGSTAmount * 100) / 100,
        PackingGSTRate: resolvedPackingGSTRate,
        PackingGSTAmount: Math.round(resolvedPackingGSTAmount * 100) / 100,
        RoundOff: Math.round(resolvedRoundOff * 100) / 100,
        TotalAmount: resolvedFinalTotal,
        BalanceAmount: resolvedBalance
      };

      console.log('Updated form data:', updatedFormData);
      return updatedFormData;
    });
  };

  // Save PO to Purchase with validation
  const handleSavePOToPurchase = async () => {
    // Validation
    const validationErrors = [];

    // Check required fields
    if (!poToPurchaseFormData.BillNumber?.trim()) {
      validationErrors.push('Bill Number is required');
    }

    if (!poToPurchaseFormData.BillDate) {
      validationErrors.push('Bill Date is required');
    }

    // Check if there are items
    if (!selectedPODetails || selectedPODetails.length === 0) {
      validationErrors.push('At least one item is required');
    }

    // Validate each item
    selectedPODetails.forEach((item, index) => {
      if (!item.ItemId) {
        validationErrors.push(`Item ${index + 1}: Please select an item`);
      }

      if (!item.ReceivedQuantity || parseFloat(item.ReceivedQuantity) <= 0) {
        validationErrors.push(`Item ${index + 1}: Received quantity must be greater than 0`);
      }

      if (!item.Rate || parseFloat(item.Rate) <= 0) {
        validationErrors.push(`Item ${index + 1}: Rate must be greater than 0`);
      }
    });

    // Check if total amount is greater than 0
    if (!poToPurchaseFormData.TotalAmount || parseFloat(poToPurchaseFormData.TotalAmount) <= 0) {
      validationErrors.push('Total amount must be greater than 0');
    }

    // Show validation errors
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase from PO...');

    try {
      // Compute totals directly from selectedPODetails to avoid stale state
      const computedAmount = selectedPODetails.reduce((s, d) => s + (parseFloat(d.Amount) || 0), 0);
      const computedDiscount = selectedPODetails.reduce((s, d) => s + (parseFloat(d.DiscountAmount) || 0), 0);
      const computedTaxable = selectedPODetails.reduce((s, d) => s + (parseFloat(d.TaxableAmount) || 0), 0);
      const computedCGST = selectedPODetails.reduce((s, d) => s + (parseFloat(d.CGSTAmount) || 0), 0);
      const computedSGST = selectedPODetails.reduce((s, d) => s + (parseFloat(d.SGSTAmount) || 0), 0);
      const computedIGST = selectedPODetails.reduce((s, d) => s + (parseFloat(d.IGSTAmount) || 0), 0);

      const dataToSend = {
        Purchase: {
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: poToPurchaseFormData.MasterId,
          MasterName: poToPurchaseFormData.MasterName,
          InvoiceTypeId: poToPurchaseFormData.InvoiceTypeId || 0,
          InvoiceTypeName: poToPurchaseFormData.InvoiceTypeName || '',
          GSTNumber: poToPurchaseFormData.GSTNumber || '',
          GstCalculationType: poToPurchaseFormData.GstCalculationType || 1,
          GstCalculationTypeName: poToPurchaseFormData.GstCalculationTypeName || 'Excluding Tax',
          PurchaseOrderId: poToPurchaseFormData.PurchaseOrderId || null,
          PurchaseOrderNo: poToPurchaseFormData.PurchaseOrderNo || '',
          PurchaseOrderDate: poToPurchaseFormData.PurchaseOrderDate || null,
          BillNumber: poToPurchaseFormData.BillNumber,
          BillDate: poToPurchaseFormData.BillDate,
          ClearingDate: poToPurchaseFormData.ClearingDate || null,
          Amount: computedAmount,
          DiscountAmount: computedDiscount,
          TaxableAmount: computedTaxable,
          CGSTAmount: computedCGST,
          SGSTAmount: computedSGST,
          IGSTAmount: computedIGST,
          FreightAmount: parseFloat(poToPurchaseFormData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(poToPurchaseFormData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(poToPurchaseFormData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(poToPurchaseFormData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(poToPurchaseFormData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(poToPurchaseFormData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(poToPurchaseFormData.TCSAmount) || 0,
          RoundOff: parseFloat(poToPurchaseFormData.RoundOff) || 0,
          TotalAmount: parseFloat(poToPurchaseFormData.TotalAmount) || 0,
          PaidAmount: parseFloat(poToPurchaseFormData.PaidAmount) || 0,
          BalanceAmount: (parseFloat(poToPurchaseFormData.TotalAmount) || 0) - (parseFloat(poToPurchaseFormData.PaidAmount) || 0),
          Remarks: poToPurchaseFormData.Remarks || '',
          AdditionalRemarks: poToPurchaseFormData.AdditionalRemarks || '',
          IsActive: true,
          CreatedBy: userId
        },
        PurchaseDetails: selectedPODetails.map(d => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          ItemId: d.ItemId,
          ItemName: d.ItemName || '',
          PurchaseOrderId: poToPurchaseFormData.PurchaseOrderId,
          OrderQuantity: d.Quantity || 0,
          ReceivedQuantity: parseFloat(d.ReceivedQuantity) || 0,
          BilledQuantity: parseFloat(d.ReceivedQuantity) || 0,
          DamageQuantity: parseFloat(d.DamageQuantity) || 0,
          ExcessQuantity: parseFloat(d.ExcessQuantity) || 0,
          LessQuantity: parseFloat(d.LessQuantity) || 0,
          StockQuantity: parseFloat(d.StockQuantity) || 0,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(d.DiscountAmount) || 0,
          HSNCode: d.HSNCode || '',
          GSTRate: parseFloat(d.GSTRate) || 0,
          TaxableAmount: parseFloat(d.TaxableAmount) || 0,
          CGSTAmount: parseFloat(d.CGSTAmount) || 0,
          SGSTAmount: parseFloat(d.SGSTAmount) || 0,
          IGSTAmount: parseFloat(d.IGSTAmount) || 0,
          TotalAmount: parseFloat(d.TotalAmount) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      await postData('PurchaseApi', '', dataToSend, token);
      setSuccess('Purchase created successfully from PO!');
      logAudit(AUDIT_ACTION.CREATE, 'Purchase From PO Request', 0, null, {
        PurchaseOrderId:  poToPurchaseFormData.PurchaseOrderId  || null,
        PurchaseOrderNo:  poToPurchaseFormData.PurchaseOrderNo  || '',
        SupplierName:     poToPurchaseFormData.MasterName       || '',
        BillNumber:       poToPurchaseFormData.BillNumber       || '',
        BillDate:         poToPurchaseFormData.BillDate         || '',
        TotalAmount:      poToPurchaseFormData.TotalAmount      || 0,
        ItemCount:        selectedPODetails.length,
      });
      handleClosePOToPurchaseModal();
      await fetchPurchases();
    } catch (error) {
      console.error('Error creating purchase from PO:', error);
      setError('Failed to create purchase from PO.');
    }

    setLoading(false);
  };

  // Activate handlers
  const handleOpenActivateModal = (id, billNo) => {
    setActivateItemId(id);
    setActivateItemNo(billNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  const handleActivate = async () => {
    if (!activateItemId) return;
    setLoading(true);
    handleCloseActivateModal();
    setLoadingMessage('Activating purchase...');
    try {
      await activateData('PurchaseApi', `${activateItemId}`, token);
      setSuccess('Purchase activated successfully!');
      await fetchAllData();
    } catch (error) {
      console.error('Error activating record:', error);
      setError('Failed to activate purchase.');
    }
    setLoading(false);
  };

  // Inactive view modal - separate styled modal like PurchaseOrder
  const handleOpenInactiveModal = async (item) => {
    setModalLoading(true);

    const freshData = await fetchInactivePurchaseDetails(item.Id);

    if (freshData) {
      const purchaseData = freshData.Purchase || freshData;
      const details = freshData.PurchaseDetails || [];
      setInactiveViewData(purchaseData);
      setInactiveViewDetails(details);
    } else {
      setInactiveViewData(item);
      setInactiveViewDetails([]);
    }

    setModalLoading(false);
    setInactiveModalOpen(true);
  };

  const handleCloseInactiveModal = () => {
    setInactiveModalOpen(false);
    setInactiveViewData(null);
    setInactiveViewDetails([]);
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    if (action === 'view') {
      const recordId = typeof itemOrId === 'object' ? (itemOrId?.Id || 0) : (itemOrId || 0);
      logAudit(AUDIT_ACTION.VIEW, 'PurchaseApi', recordId);
    }
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSinglePurchase(itemId);

      if (freshData) {
        const p = freshData.Purchase || freshData;
        const details = freshData.PurchaseDetails || [];
        const supplierName = p.MasterName || suppliers.find(s => (s.MasterId || s.Id) === p.MasterId)?.Name || '';
        const supplier = suppliers.find(s => (s.MasterId || s.Id) === p.MasterId);
        const invoiceTypeName = p.InvoiceTypeName || supplier?.InvoiceTypeName || '';

        setSelectedItem(p);
        setFormData({
          Id: p.Id,
          CompanyId: p.CompanyId || companyId,
          BranchId: p.BranchId || branchId,
          StockBranchId: p.StockBranchId || '',
          MasterId: p.MasterId,
          MasterName: supplierName,
          InvoiceTypeId: p.InvoiceTypeId || 0,
          InvoiceTypeName: invoiceTypeName,
          GSTNumber: p.GSTNumber || '',
          GstCalculationType: p.GstCalculationType || 1,
          GstCalculationTypeName: p.GstCalculationTypeName || 'Excluding Tax',
          // PO Reference
          PurchaseOrderId: p.PurchaseOrderId || null,
          PurchaseOrderNo: p.PurchaseOrderNo || '',
          PurchaseOrderDate: p.PurchaseOrderDate?.split('T')[0] || '',
          // Purchase Details
          BillDate: p.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          ClearingDate: p.ClearingDate?.split('T')[0] || '',
          BillNumber: p.BillNumber || '',
          // Calculation Fields
          TotalQuantity: p.TotalQuantity || 0,
          Amount: p.Amount || 0,
          DiscountAmount: p.DiscountAmount || 0,
          TaxableAmount: p.TaxableAmount || 0,
          CGSTAmount: p.CGSTAmount || 0,
          SGSTAmount: p.SGSTAmount || 0,
          IGSTAmount: p.IGSTAmount || 0,
          FreightAmount: p.FreightAmount || 0,
          FreightGSTRate: p.FreightGSTRate || (p.FreightAmount && p.FreightGSTAmount ? Math.round((p.FreightGSTAmount / p.FreightAmount) * 100) : 0),
          FreightGSTAmount: p.FreightGSTAmount || 0,
          PackingAmount: p.PackingAmount || 0,
          PackingGSTRate: p.PackingGSTRate || (p.PackingAmount && p.PackingGSTAmount ? Math.round((p.PackingGSTAmount / p.PackingAmount) * 100) : 0),
          PackingGSTAmount: p.PackingGSTAmount || 0,
          TCSAmount: p.TCSAmount || 0,
          RoundOff: p.RoundOff || 0,
          TotalAmount: p.TotalAmount || 0,
          PaidAmount: p.PaidAmount || 0,
          BalanceAmount: p.BalanceAmount || 0,
          Remarks: p.Remarks || '',
          IsActive: p.IsActive ?? true,
          IsVerified: p.IsVerified ?? false,
          IsConvertToGoodReceipt: p.IsConvertToGoodReceipt ?? false,
          CreatedBy: p.CreatedBy,
          CreatedByName: p.CreatedByName || '',
          CreatedDate: p.CreatedDate,
          ModifiedBy: p.ModifiedBy,
          ModifiedByName: p.ModifiedByName || '',
          ModifiedDate: p.ModifiedDate
        });

        setPurchaseDetails(details.map(d => ({
          Id: d.Id || 0,
          PurchaseId: d.PurchaseId || 0,
          CompanyId: d.CompanyId || companyId,
          BranchId: d.BranchId || branchId,
          ItemId: d.ItemId,
          ItemName: d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '',
          BatchNumber: d.BatchNumber || '',
          PurchaseOrderId: d.PurchaseOrderId || null,
          // Quantity fields
          OrderQuantity: d.OrderQuantity || 0,
          ReceivedQuantity: d.ReceivedQuantity || 0,
          BilledQuantity: d.BilledQuantity || 0,
          DamageQuantity: d.DamageQuantity || 0,
          ExcessQuantity: d.ExcessQuantity || 0,
          LessQuantity: d.LessQuantity || 0,
          StockQuantity: d.StockQuantity || 0,
          ReturnDamageQuantity: d.ReturnDamageQuantity || 0,
          ReturnStockQuantity: d.ReturnStockQuantity || 0,
          // Calculation fields
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy,
          CreatedDate: d.CreatedDate,
          ModifiedBy: d.ModifiedBy,
          ModifiedDate: d.ModifiedDate
        })));
      } else {
        setSelectedItem(null);
        setFormData({});
        setPurchaseDetails([]);
      }
      setModalLoading(false);
    } else {
      // Create mode
      setModalLoading(true);
      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();
      const freightGSTRate = gstData.freight?.GSTRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        StockBranchId: branchId || '',
        MasterId: '',
        InvoiceTypeId: 0,
        GSTNumber: '',
        PurchaseOrderId: null,
        PurchaseOrderNo: '',
        PurchaseOrderDate: '',
        BillDate: new Date().toISOString().split('T')[0],
        ClearingDate: new Date().toISOString().split('T')[0],
        BillNumber: '',
        TotalQuantity: 0,
        Amount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: freightGSTRate,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: packingGSTRate,
        PackingGSTAmount: 0,
        TCSAmount: 0,
        DiscountAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        PaidAmount: 0,
        BalanceAmount: 0,
        Remarks: '',
        IsActive: true,
        IsVerified: false,
        IsConvertToGoodReceipt: false
      });
      setPurchaseDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setPurchaseDetails([]);
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} purchase...`);

      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
      const masterId = selectedSupplier?.MasterId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || formData.InvoiceTypeId || 0;
      const gstNumber = selectedSupplier?.GSTNumber || selectedSupplier?.GSTNo || '';

      const totalQuantity = purchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ReceivedQuantity) || 0), 0);

      // Compute header totals directly from purchaseDetails to avoid stale formData state
      const computedAmount = purchaseDetails.reduce((s, d) => s + (parseFloat(d.Amount) || 0), 0);
      const computedDiscount = purchaseDetails.reduce((s, d) => s + (parseFloat(d.DiscountAmount) || 0), 0);
      const computedTaxable = purchaseDetails.reduce((s, d) => s + (parseFloat(d.TaxableAmount) || 0), 0);
      const computedCGST = purchaseDetails.reduce((s, d) => s + (parseFloat(d.CGSTAmount) || 0), 0);
      const computedSGST = purchaseDetails.reduce((s, d) => s + (parseFloat(d.SGSTAmount) || 0), 0);
      const computedIGST = purchaseDetails.reduce((s, d) => s + (parseFloat(d.IGSTAmount) || 0), 0);

      const dataToSend = {
        Purchase: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || branchId,
          StockBranchId: formData.StockBranchId || null,
          MasterId: masterId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          GSTNumber: gstNumber,
          GstCalculationType: formData.GstCalculationType || 1,
          GstCalculationTypeName: formData.GstCalculationTypeName || 'Excluding Tax',
          // PO Reference
          PurchaseOrderId: formData.PurchaseOrderId || null,
          PurchaseOrderNo: formData.PurchaseOrderNo || '',
          PurchaseOrderDate: formData.PurchaseOrderDate || null,
          // Purchase Details
          BillDate: formData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: formData.ClearingDate || null,
          BillNumber: formData.BillNumber || '',
          // Calculation Fields
          TotalQuantity: totalQuantity,
          Amount: computedAmount,
          DiscountAmount: computedDiscount,
          TaxableAmount: computedTaxable,
          CGSTAmount: computedCGST,
          SGSTAmount: computedSGST,
          IGSTAmount: computedIGST,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(formData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(formData.TCSAmount) || 0,
          RoundOff: parseFloat((parseFloat(formData.RoundOff) || 0).toFixed(2)),
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          PaidAmount: parseFloat(formData.PaidAmount) || 0,
          BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
          Remarks: formData.Remarks || '',
          IsActive: formData.IsActive ?? true,
          IsVerified: formData.IsVerified ?? false,
          IsConvertToGoodReceipt: formData.IsConvertToGoodReceipt ?? false,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'edit' ? formData.CreatedDate : null,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        PurchaseDetails: purchaseDetails.map(d => {
          const selectedItem = items.find(i => i.Id === parseInt(d.ItemId));
          return {
            Id: modalAction === 'edit' ? (d.Id || 0) : 0,
            PurchaseId: modalAction === 'edit' ? formData.Id : 0,
            CompanyId: companyId,
            BranchId: formData.BranchId || branchId,
            ItemId: parseInt(d.ItemId) || 0,
            ItemName: selectedItem?.Name || d.ItemName || '',
            BatchNumber: d.BatchNumber || '',
            PurchaseOrderId: d.PurchaseOrderId || null,
            // Quantity fields
            OrderQuantity: parseFloat(d.OrderQuantity) || 0,
            ReceivedQuantity: parseFloat(d.ReceivedQuantity) || 0,
            BilledQuantity: parseFloat(d.BilledQuantity) || 0,
            DamageQuantity: parseFloat(d.DamageQuantity) || 0,
            ExcessQuantity: parseFloat(d.ExcessQuantity) || 0,
            LessQuantity: parseFloat(d.LessQuantity) || 0,
            StockQuantity: parseFloat(d.StockQuantity) || 0,
            ReturnDamageQuantity: parseFloat(d.ReturnDamageQuantity) || 0,
            ReturnStockQuantity: parseFloat(d.ReturnStockQuantity) || 0,
            // Calculation fields
            Rate: parseFloat(d.Rate) || 0,
            Amount: parseFloat(d.Amount) || 0,
            DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(d.DiscountAmount) || 0,
            HSNCode: d.HSNCode || '',
            GSTRate: parseFloat(d.GSTRate) || 0,
            TaxableAmount: parseFloat(d.TaxableAmount) || 0,
            CGSTAmount: parseFloat(d.CGSTAmount) || 0,
            SGSTAmount: parseFloat(d.SGSTAmount) || 0,
            IGSTAmount: parseFloat(d.IGSTAmount) || 0,
            TotalAmount: parseFloat(d.TotalAmount) || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: modalAction === 'create' ? userId : d.CreatedBy,
            CreatedDate: modalAction === 'edit' ? d.CreatedDate : null,
            ModifiedBy: modalAction === 'edit' ? userId : null
          };
        })
      };

      if (modalAction === 'create') {
        const response = await postData('PurchaseApi', '', dataToSend, token);
        console.log('Create response:', response);
        setSuccess('Purchase created successfully!');
        logAudit(AUDIT_ACTION.CREATE, 'PurchaseApi', 0, null, {
          SupplierName:  formData.MasterName       || '',
          BillNumber:    formData.BillNumber        || '',
          BillDate:      formData.BillDate          || '',
          TotalAmount:   formData.TotalAmount       || 0,
          ItemCount:     purchaseDetails.length,
        });
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Purchase ID is missing');
        const response = await updateData('PurchaseApi', formData.Id, dataToSend, token);
        console.log('Update response:', response);
        setSuccess('Purchase updated successfully!');
        logAudit(AUDIT_ACTION.UPDATE, 'PurchaseApi', formData.Id, { ...selectedItem }, { ...formData });
      }

      handleCloseModal();
      await fetchPurchases();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} purchase. Please try again.`);
      setLoading(false);
    }
  };

  // Supplier drill-down handler
  const handleSupplierClick = async (masterId, supplierName) => {
    setLoading(true);
    setLoadingMessage('Fetching supplier purchases...');
    try {
      const response = await fetchData('PurchaseApi', `/PurchaseHistoryByMaster/${masterId}/${branchId}`, token);
      const responseData = response.response_data || response || [];
      let purchasesList = [], purchaseDetailsList = [];
      if (Array.isArray(responseData) && responseData.length > 0) {
        purchasesList = responseData[0].Purchase || [];
        purchaseDetailsList = responseData[0].PurchaseDetails || [];
      }
      const purchasesWithDetails = purchasesList.map(p => ({
        ...p,
        PurchaseDetails: purchaseDetailsList.filter(d => d.PurchaseId === p.Id)
      }));
      setSelectedSupplier({ id: masterId, name: supplierName });
      setSupplierPurchases(purchasesWithDetails);
      setExpandedPurchaseId(null);
      setSupplierModalOpen(true);
    } catch (error) {
      console.error('Error fetching supplier purchases:', error);
      const filteredPurchases = purchases.filter(p => p.MasterId === masterId);
      setSelectedSupplier({ id: masterId, name: supplierName });
      setSupplierPurchases(filteredPurchases);
      setSupplierModalOpen(true);
    }
    setLoading(false);
  };

  const handleCloseSupplierModal = () => {
    setSupplierModalOpen(false);
    setSelectedSupplier(null);
    setSupplierPurchases([]);
    setExpandedPurchaseId(null);
  };

  const togglePurchaseExpand = (purchaseId) => {
    setExpandedPurchaseId(expandedPurchaseId === purchaseId ? null : purchaseId);
  };

  // Handle purchase row drill-down toggle with animation
  const togglePurchaseRowExpand = (purchaseId) => {
    setExpandedPurchaseRowId(expandedPurchaseRowId === purchaseId ? null : purchaseId);
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, billNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(billNo);
    setDeleteModalOpen(true);
  };

  // ── Open Payment View Modal ─────────────────────────────────────────────
  const handleOpenPaymentModal = async (paymentId) => {
    if (!paymentId) return;
    setPaymentViewLoading(true);
    setPaymentViewModal({ open: true });
    setPaymentPopupAnchor(null);
    setPaymentPopupData(null);
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${paymentId}`, token);
      const freshData = response.response_data || response;
      if (freshData) {
        const p = freshData.PaymentAndReceipt || freshData;
        setPaymentViewFormData({
          ...p,
          BillDate: p.BillDate ? p.BillDate.split('T')[0] : '',
          ChequeDate: p.ChequeDate ? p.ChequeDate.split('T')[0] : '',
          MasterId: p.MasterId || '',
        });
        const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
        const saleDetails = freshData.PaymentAndReceiptSale || [];
        if (p.IsType && purchaseDetails.length > 0) {
          setPaymentViewDetails(purchaseDetails.map(d => ({
            Id: d.PurchaseId,
            Description: d.PurchaseDescription || d.Description || `Purchase - ${d.PurchaseId}`,
            Amount: parseFloat(d.PurchaseAmount || d.BillAmount || 0),
            PaidAmount: parseFloat(d.PaidAmount || 0),
            PendingAmount: parseFloat(d.PendingAmount || 0),
            BillDate: d.BillDate || p.BillDate,
            Type: 'Purchase',
            PaymentAmount: parseFloat(d.Amount || 0),
            IsSelected: true,
          })));
        } else if (!p.IsType && saleDetails.length > 0) {
          setPaymentViewDetails(saleDetails.map(d => ({
            Id: d.SaleId,
            Description: d.SaleDescription || d.Description || `Sale - ${d.SaleId}`,
            Amount: parseFloat(d.SaleAmount || d.BillAmount || 0),
            PaidAmount: parseFloat(d.PaidAmount || 0),
            PendingAmount: parseFloat(d.PendingAmount || 0),
            BillDate: d.BillDate || p.BillDate,
            Type: 'Sale',
            PaymentAmount: parseFloat(d.Amount || 0),
            IsSelected: true,
          })));
        } else {
          setPaymentViewDetails([]);
        }
      }
    } catch (err) {
      console.error('Error fetching payment details:', err);
    } finally {
      setPaymentViewLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;
    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('PurchaseApi', deleteItemId, token);
      setSuccess('Purchase deleted successfully!');
      logAudit(AUDIT_ACTION.DELETE, 'PurchaseApi', deleteItemId, { Id: deleteItemId }, null);
      await fetchPurchases();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete purchase.');
    }
    setLoading(false);
  };

  // Convert to Good Receipt handlers
  const handleOpenConvertModal = (id, billNo) => {
    setConvertItemId(id);
    setConvertItemNo(billNo);
    setConvertModalOpen(true);
  };

  const handleCloseConvertModal = () => {
    setConvertModalOpen(false);
    setConvertItemId(null);
    setConvertItemNo('');
  };

  const handleToGoodReceipt = async () => {
    if (!convertItemId) return;
    setLoading(true);
    handleCloseConvertModal();
    setLoadingMessage('Converting to Good Receipt...');
    try {
      await ConvertToGoodReceipt('PurchaseApi', convertItemId, token);
      setSuccess('Purchase converted to Good Receipt successfully!');
      logAudit(AUDIT_ACTION.APPROVE, 'PurchaseApi', convertItemId);
      handleCloseModal(); // Close the modal after successful conversion
      await fetchPurchases();
    } catch (error) {
      console.error('Error converting to Good Receipt:', error);
      setError('Failed to convert purchase to Good Receipt');
    }
    setLoading(false);
  };

  const handleVerifyFromModal = async (purchaseId) => {
    // Close the view modal first, then open the verify modal
    handleCloseModal();
    const purchaseItem = purchases.find(p => p.Id === purchaseId);
    if (purchaseItem) {
      await handleOpenVerifyModal(purchaseItem);
    }
  };

  // Handle Update & Save from Good Receipt Modal (same as edit function)
  const handleUpdateFromGoodReceipt = async (purchaseId, updatedPurchaseDetails) => {
    if (!updatedPurchaseDetails || !verifyPurchaseData) {
      setError('Purchase details are required for update.');
      return;
    }

    setLoading(true);
    setLoadingMessage('Updating Good Receipt details...');

    try {
      // Use the same logic as handleSave but for updating from Good Receipt modal
      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(verifyPurchaseData.MasterId));
      const masterId = selectedSupplier?.MasterId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || verifyPurchaseData.InvoiceTypeId || 0;
      const gstNumber = selectedSupplier?.GSTNumber || selectedSupplier?.GSTNo || '';

      const totalQuantity = updatedPurchaseDetails.reduce((sum, d) => sum + (parseFloat(d.ReceivedQuantity) || 0), 0);

      const dataToSend = {
        Purchase: {
          Id: verifyPurchaseData.Id,
          CompanyId: companyId,
          BranchId: verifyPurchaseData.BranchId || branchId,
          StockBranchId: verifyPurchaseData.StockBranchId || null,
          MasterId: masterId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          GSTNumber: gstNumber,
          GstCalculationType: verifyPurchaseData.GstCalculationType || 1,
          GstCalculationTypeName: verifyPurchaseData.GstCalculationTypeName || 'Excluding Tax',
          // PO Reference
          PurchaseOrderId: verifyPurchaseData.PurchaseOrderId || null,
          PurchaseOrderNo: verifyPurchaseData.PurchaseOrderNo || '',
          PurchaseOrderDate: verifyPurchaseData.PurchaseOrderDate || null,
          // Purchase Details
          BillDate: verifyPurchaseData.BillDate || new Date().toISOString().split('T')[0],
          ClearingDate: verifyPurchaseData.ClearingDate || null,
          BillNumber: verifyPurchaseData.BillNumber || '',
          // Calculation Fields
          TotalQuantity: totalQuantity,
          Amount: parseFloat(verifyPurchaseData.Amount) || 0,
          CGSTAmount: parseFloat(verifyPurchaseData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(verifyPurchaseData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(verifyPurchaseData.IGSTAmount) || 0,
          FreightAmount: parseFloat(verifyPurchaseData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(verifyPurchaseData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(verifyPurchaseData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(verifyPurchaseData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(verifyPurchaseData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(verifyPurchaseData.PackingGSTAmount) || 0,
          TCSAmount: parseFloat(verifyPurchaseData.TCSAmount) || 0,
          DiscountAmount: parseFloat(verifyPurchaseData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(verifyPurchaseData.TaxableAmount) || 0,
          RoundOff: Math.round((parseFloat(verifyPurchaseData.RoundOff) || 0) * 100) / 100,
          TotalAmount: parseFloat(verifyPurchaseData.TotalAmount) || 0,
          PaidAmount: parseFloat(verifyPurchaseData.PaidAmount) || 0,
          BalanceAmount: parseFloat(verifyPurchaseData.BalanceAmount) || 0,
          Remarks: verifyPurchaseData.Remarks || '',
          IsActive: verifyPurchaseData.IsActive ?? true,
          IsVerified: verifyPurchaseData.IsVerified ?? false,
          IsConvertToGoodReceipt: true, // Always set to true when updating from Good Receipt modal
          CreatedBy: verifyPurchaseData.CreatedBy,
          CreatedDate: verifyPurchaseData.CreatedDate,
          ModifiedBy: userId
        },
        PurchaseDetails: updatedPurchaseDetails.map(d => {
          const selectedItem = items.find(i => i.Id === parseInt(d.ItemId));
          return {
            Id: d.Id || 0,
            PurchaseId: verifyPurchaseData.Id,
            CompanyId: companyId,
            BranchId: verifyPurchaseData.BranchId || branchId,
            ItemId: parseInt(d.ItemId) || 0,
            ItemName: selectedItem?.Name || d.ItemName || '',
            BatchNumber: d.BatchNumber || '',
            PurchaseOrderId: d.PurchaseOrderId || null,
            // Quantity fields
            OrderQuantity: parseFloat(d.OrderQuantity) || 0,
            ReceivedQuantity: parseFloat(d.ReceivedQuantity) || 0,
            BilledQuantity: parseFloat(d.BilledQuantity) || 0,
            DamageQuantity: parseFloat(d.DamageQuantity) || 0,
            ExcessQuantity: parseFloat(d.ExcessQuantity) || 0,
            LessQuantity: parseFloat(d.LessQuantity) || 0,
            StockQuantity: parseFloat(d.StockQuantity) || 0,
            ReturnDamageQuantity: parseFloat(d.ReturnDamageQuantity) || 0,
            ReturnStockQuantity: parseFloat(d.ReturnStockQuantity) || 0,
            // Calculation fields
            Rate: parseFloat(d.Rate) || 0,
            Amount: parseFloat(d.Amount) || 0,
            DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(d.DiscountAmount) || 0,
            HSNCode: d.HSNCode || '',
            GSTRate: parseFloat(d.GSTRate) || 0,
            TaxableAmount: parseFloat(d.TaxableAmount) || 0,
            CGSTAmount: parseFloat(d.CGSTAmount) || 0,
            SGSTAmount: parseFloat(d.SGSTAmount) || 0,
            IGSTAmount: parseFloat(d.IGSTAmount) || 0,
            TotalAmount: parseFloat(d.TotalAmount) || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy,
            CreatedDate: d.CreatedDate,
            ModifiedBy: userId
          };
        })
      };

      // Use the same update API as edit function
      await updateData('PurchaseApi', verifyPurchaseData.Id, dataToSend, token);
      setSuccess('Good Receipt updated successfully! Changes have been saved.');

      // Refresh the purchase data to show updated values
      const freshData = await fetchSinglePurchase(verifyPurchaseData.Id);
      if (freshData) {
        const p = freshData.Purchase || freshData;
        const details = freshData.PurchaseDetails || [];
        setVerifyPurchaseData(p);
        setVerifyPurchaseDetails(details);
      }

      await fetchPurchases();

    } catch (error) {
      console.error('Error updating purchase:', error);
      setError('Failed to update purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle actual verification (with confirmation)
  const handleActualVerify = async (purchaseId, updatedPurchaseDetails = null) => {
    if (!purchaseId) return;

    setLoading(true);
    setLoadingMessage('Processing Good Receipt verification...');

    try {
      // First update the purchase details if any changes were made
      if (updatedPurchaseDetails && updatedPurchaseDetails.length > 0) {
        await handleUpdateFromGoodReceipt(purchaseId, updatedPurchaseDetails);
      }

      // Then verify the purchase (this will create stock entries)
      await ConvertPurchaseVerify('PurchaseApi', purchaseId, token);
      setSuccess('Good Receipt verified successfully! Stock has been updated and purchase is now complete.');
      logAudit(AUDIT_ACTION.VERIFY, 'PurchaseApi', purchaseId);
      handleCloseVerifyModal();
      await fetchPurchases();

    } catch (error) {
      console.error('Error verifying purchase:', error);
      setError('Failed to verify Good Receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify handler
  const handleVerify = async (purchaseId, updatedPurchaseDetails = null) => {
    if (!updatedPurchaseDetails || !verifyPurchaseData) {
      setError('Purchase details are required for verification.');
      return;
    }

    setLoading(true);
    setLoadingMessage('Processing good receipt...');

    try {
      // Prepare the PurchaseVerifyVM structure
      const verifyData = {
        Purchase: {
          ...verifyPurchaseData,
          IsVerified: true,
          ModifiedBy: userId,
          ModifiedDate: new Date().toISOString()
        },
        PurchaseDetails: updatedPurchaseDetails.map(detail => ({
          ...detail,
          ModifiedBy: userId,
          ModifiedDate: new Date().toISOString()
        })),
        // Stock: updatedPurchaseDetails.map(detail => ({
        //   Id: 0, // New stock entry
        //   CompanyId: companyId,
        //   BranchId: branchId,
        //   PurchaseDetailsId: detail.Id,
        //   StockType: 1, // 1 for Purchase
        //   ItemId: detail.ItemId,
        //   UOMId: null, // You can set this if you have UOM data
        //   Quantity: parseFloat(detail.StockQuantity) || 0,
        //   BalanceQuantity: parseFloat(detail.StockQuantity) || 0,
        //   DamageQuantity: parseFloat(detail.DamageQuantity) || 0,
        //   DamageBalanceQuantity: parseFloat(detail.DamageQuantity) || 0,
        //   CGSTRate: parseFloat(detail.GSTRate) / 2 || 0, // Assuming CGST is half of total GST
        //   SGSTRate: parseFloat(detail.GSTRate) / 2 || 0, // Assuming SGST is half of total GST
        //   HSNCode: detail.HSNCode || '',
        //   BatchNumber: detail.BatchNumber || '',
        //   IsReturnable: true, // Default values - you can customize these
        //   ReturnDays: 30,
        //   HasWarranty: false,
        //   WarrantyDays: 0,
        //   StockInDate: new Date().toISOString().split('T')[0],
        //   ReturnDate: null,
        //   WarrantyEndDate: null,
        //   PurchaseRate: parseFloat(detail.Rate) || 0,
        //   IsActive: true,
        //   CreatedBy: userId,
        //   CreatedDate: new Date().toISOString()
        // }))
      };

      // Use the new API endpoint with PurchaseVerifyVM
      const response = await fetch(`${apiConfig.apiUrl}PurchaseApi/${purchaseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(verifyData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setSuccess('Good receipt processed successfully with stock updated!');
      logAudit(AUDIT_ACTION.VERIFY, 'PurchaseApi', purchaseId);
      handleCloseVerifyModal();
      await fetchPurchases();

    } catch (error) {
      console.error('Error verifying purchase:', error);
      setError('Failed to process good receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verify modal handlers
  const handleOpenVerifyModal = async (item) => {
    setModalLoading(true);

    const freshData = await fetchSinglePurchase(item.Id);

    if (freshData) {
      const p = freshData.Purchase || freshData;
      const details = freshData.PurchaseDetails || [];
      const supplierName = p.MasterName || suppliers.find(s => (s.MasterId || s.Id) === p.MasterId)?.Name || '';
      const supplier = suppliers.find(s => (s.MasterId || s.Id) === p.MasterId);
      const invoiceTypeName = p.InvoiceTypeName || supplier?.InvoiceTypeName || '';

      setVerifyPurchaseData({
        Id: p.Id,
        CompanyId: p.CompanyId || companyId,
        BranchId: p.BranchId || branchId,
        StockBranchId: p.StockBranchId || '',
        MasterId: p.MasterId,
        MasterName: supplierName,
        InvoiceTypeId: p.InvoiceTypeId || 0,
        InvoiceTypeName: invoiceTypeName,
        GSTNumber: p.GSTNumber || '',
        PurchaseOrderId: p.PurchaseOrderId || null,
        PurchaseOrderNo: p.PurchaseOrderNo || '',
        PurchaseOrderDate: p.PurchaseOrderDate?.split('T')[0] || p.PurchaseOrderDate || null,
        BillDate: p.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
        ClearingDate: p.ClearingDate?.split('T')[0] || '',
        BillNumber: p.BillNumber || '',
        TotalQuantity: p.TotalQuantity || 0,
        Amount: p.Amount || 0,
        DiscountAmount: p.DiscountAmount || 0,
        TaxableAmount: p.TaxableAmount || 0,
        CGSTAmount: p.CGSTAmount || 0,
        SGSTAmount: p.SGSTAmount || 0,
        IGSTAmount: p.IGSTAmount || 0,
        FreightAmount: p.FreightAmount || 0,
        FreightGSTRate: p.FreightGSTRate || (p.FreightAmount && p.FreightGSTAmount ? Math.round((p.FreightGSTAmount / p.FreightAmount) * 100) : 0),
        FreightGSTAmount: p.FreightGSTAmount || 0,
        PackingAmount: p.PackingAmount || 0,
        PackingGSTRate: p.PackingGSTRate || (p.PackingAmount && p.PackingGSTAmount ? Math.round((p.PackingGSTAmount / p.PackingAmount) * 100) : 0),
        PackingGSTAmount: p.PackingGSTAmount || 0,
        TCSAmount: p.TCSAmount || 0,
        RoundOff: p.RoundOff || 0,
        TotalAmount: p.TotalAmount || 0,
        PaidAmount: p.PaidAmount || 0,
        BalanceAmount: p.BalanceAmount || 0,
        Remarks: p.Remarks || '',
        IsActive: p.IsActive ?? true,
        IsVerified: p.IsVerified ?? false,
        CreatedBy: p.CreatedBy,
        CreatedByName: p.CreatedByName || '',
        CreatedDate: p.CreatedDate,
        ModifiedBy: p.ModifiedBy,
        ModifiedByName: p.ModifiedByName || '',
        ModifiedDate: p.ModifiedDate
      });

      setVerifyPurchaseDetails(details.map(d => ({
        Id: d.Id || 0,
        PurchaseId: d.PurchaseId || 0,
        CompanyId: d.CompanyId || companyId,
        BranchId: d.BranchId || branchId,
        ItemId: d.ItemId,
        ItemName: d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '',
        BatchNumber: d.BatchNumber || '',
        ReceivedQuantity: d.ReceivedQuantity || 0,
        BilledQuantity: d.BilledQuantity || 0,
        DamageQuantity: d.DamageQuantity || 0,
        ExcessQuantity: d.ExcessQuantity || 0,
        LessQuantity: d.LessQuantity || 0,
        StockQuantity: d.StockQuantity || 0,
        ReturnDamageQuantity: d.ReturnDamageQuantity || 0,
        ReturnStockQuantity: d.ReturnStockQuantity || 0,
        Rate: d.Rate || 0,
        Amount: d.Amount || 0,
        DiscountPercentage: d.DiscountPercentage || 0,
        DiscountAmount: d.DiscountAmount || 0,
        HSNCode: d.HSNCode || '',
        GSTRate: d.GSTRate || 0,
        TaxableAmount: d.TaxableAmount || 0,
        CGSTAmount: d.CGSTAmount || 0,
        SGSTAmount: d.SGSTAmount || 0,
        IGSTAmount: d.IGSTAmount || 0,
        TotalAmount: d.TotalAmount || 0,
        IsActive: d.IsActive ?? true,
        CreatedBy: d.CreatedBy,
        CreatedDate: d.CreatedDate,
        ModifiedBy: d.ModifiedBy,
        ModifiedDate: d.ModifiedDate
      })));
    } else {
      setVerifyPurchaseData(null);
      setVerifyPurchaseDetails([]);
    }

    setModalLoading(false);
    setVerifyModalOpen(true);
  };

  const handleCloseVerifyModal = () => {
    setVerifyModalOpen(false);
    setVerifyPurchaseData(null);
    setVerifyPurchaseDetails([]);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];
    let filtered = data;
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.BillNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        suppliers.find(s => s.MasterId === item.MasterId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => filterStatus === 'active' ? item.IsActive : !item.IsActive);
    }
    if (filterVerified !== 'all') {
      if (filterVerified === 'verified') {
        filtered = filtered.filter(item => item.IsConvertToGoodReceipt && !item.IsVerified);
      } else if (filterVerified === 'verified-status') {
        filtered = filtered.filter(item => item.IsVerified);
      } else if (filterVerified === 'pending') {
        filtered = filtered.filter(item => !item.IsConvertToGoodReceipt);
      }
    }
    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };

  const formatCurrency = (amt) => `₹${(parseFloat(amt) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getSupplierName = (item) => {
    if (item.MasterName) return item.MasterName;
    const supplier = suppliers.find(s => (s.MasterId || s.Id) === item.MasterId);
    return supplier?.Name || '-';
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(purchases);


  // Stats
  const totalPurchases = purchases.length;
  const activePurchases = purchases.filter(p => p.IsActive).length;
  const verifiedPurchases = purchases.filter(p => p.IsVerified).length;
  const totalAmount = purchases.filter(p => p.IsActive).reduce((s, p) => s + (p.TotalAmount || 0), 0);

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading} message={loadingMessage} variant="default" />

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Romans', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Purchase</h4>
        </div>

        {/* Stats Cards */}
        {/* <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Purchases', value: totalPurchases, color: '#2C3E6E' },
            { label: 'Active', value: activePurchases, color: '#16a34a' },
            { label: 'Verified', value: verifiedPurchases, color: '#0891b2' },
            { label: 'Total Amount', value: `₹${totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: '#7c3aed' },
          ].map((card, i) => (
            <div key={i} style={{ flex: '1 1 120px', background: '#f8f9fa', borderRadius: '8px', padding: '8px 12px', borderLeft: `3px solid ${card.color}` }}>
              <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{card.label}</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: card.color, marginTop: '2px' }}>{card.value}</div>
            </div>
          ))}
        </div> */}

        {/* Search, Filters and Buttons */}
        {isMobile ? (
          /* ── Mobile Layout ── */
          <Box sx={{ mb: 1.5 }}>
            {/* Row 1: icon bar — horizontally scrollable */}
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, overflowY: 'visible' }}>
              {/* Search toggle */}
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              {/* From date */}
              <input type="date" lang="en-GB" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              {/* To date */}
              <input type="date" lang="en-GB" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              {/* Status filter */}
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {/* Verified filter */}
              <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All</option>
                <option value="verified">Good Receipt</option>
                <option value="verified-status">Verified</option>
                <option value="pending">Pending</option>
              </select>
              {/* Refresh */}
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={fetchAllData} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              {hasCreateMasterPermission && (
                <>
                  {/* PO Request */}
                  <Tooltip title="PO Requests" arrow>
                    <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                      <IconButton onClick={handleOpenConvertedPOModal} size="small" sx={{ background: '#8b5cf6', color: 'white', borderRadius: '8px', height: '32px', width: '32px', '&:hover': { background: '#7c3aed' } }}>
                        <Assignment sx={{ fontSize: '16px' }} />
                      </IconButton>
                      {requestCount > 0 && (
                        <Box sx={{ position: 'absolute', bottom: -6, right: -6, backgroundColor: '#ef4444', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold', border: '2px solid white', zIndex: 1 }}>
                          {requestCount > 99 ? '99+' : requestCount}
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                  {/* Add Purchase */}
                  <Tooltip title="Add Purchase" arrow>
                    <IconButton onClick={() => handleOpenModal('create')} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                      <Add sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
            {/* Row 2: Search field — shown when search icon clicked */}
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="Search by Bill No, Supplier..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          /* ── Desktop Layout ── */
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by Bill No, Supplier..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" lang="en-GB" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" lang="en-GB" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All</option>
              <option value="verified">Good Receipt</option>
              <option value="verified-status">Verified</option>
              <option value="pending">Pending</option>
            </select>
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={fetchAllData} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px', flexShrink: 0 }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            {hasCreateMasterPermission && (
              <>
                <Tooltip title="PO Requests" arrow>
                  <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                    <Button variant="contained" onClick={handleOpenConvertedPOModal} sx={{ background: '#8b5cf6', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { background: '#7c3aed' } }}>
                      <Assignment sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Request</span>
                    </Button>
                    {requestCount > 0 && (
                      <Box sx={{ position: 'absolute', top: -6, right: -6, backgroundColor: '#ef4444', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold', border: '2px solid white' }}>
                        {requestCount > 99 ? '99+' : requestCount}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
                <Tooltip title="Add Purchase" arrow>
                  <Button variant="contained" onClick={() => handleOpenModal('create')} sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Add Purchase</span>
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Bill No', 'Bill Date', 'Supplier', 'Total Amount', 'Pmt Status', 'Receipt Status', 'Status', 'PR', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 9 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <>
                    <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                        <span onClick={() => togglePurchaseRowExpand(item.Id)} style={{ cursor: 'pointer', color: '#2C3E6E', textDecoration: 'underline' }}>{item.BillNumber || '-'}</span>
                        <IconButton size="small" onClick={() => togglePurchaseRowExpand(item.Id)} sx={{ p: 0.3, ml: 0.5 }}>
                          {expandedPurchaseRowId === item.Id ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.BillDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{getSupplierName(item)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>{formatCurrency(item.TotalAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'center' }}>
                        <Chip
                          label={item.PaymentStatus || '-'}
                          size="small"
                          onClick={(e) => {
                            setPaymentPopupAnchor(e.currentTarget);
                            setPaymentPopupData(item);
                          }}
                          sx={{
                            fontSize: '0.65rem',
                            height: '22px',
                            cursor: 'pointer',
                            fontWeight: 600,
                            bgcolor: item.PaymentStatus === 'Paid' ? '#d1fae5' : item.PaymentStatus === 'Partial' ? '#fef3c7' : '#fee2e2',
                            color: item.PaymentStatus === 'Paid' ? '#065f46' : item.PaymentStatus === 'Partial' ? '#92400e' : '#991b1b',
                            '&:hover': { opacity: 0.8 }
                          }}
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsConvertToGoodReceipt ? 'Received' : 'Pending'} color={item.IsConvertToGoodReceipt ? 'success' : 'warning'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        {item.IsActive ? (
                          item.IsVerified ? <Chip label="Verified" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#8b5cf6', color: 'white' }} />
                            : item.IsConvertToGoodReceipt ? <Chip label="Good Receipt" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                              : <Chip label="Active" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        ) : <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'center' }}>
                        {item.IsActive && item.IsVerified ? (
                          <Tooltip title="Create Purchase Return" arrow>
                            <Button size="small" onClick={() => handleOpenPurchaseReturnModal(item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>↩️</Button>
                          </Tooltip>
                        ) : <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>-</span>}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {item.IsActive ? (
                            <>
                              <Tooltip title="View" arrow><Button size="small" onClick={() => handleOpenModal('view', item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button></Tooltip>
                              {hasEditMasterPermission && !item.IsConvertToGoodReceipt && <Tooltip title="Edit" arrow><Button size="small" onClick={() => handleOpenModal('edit', item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button></Tooltip>}
                              {hasEditMasterPermission && item.IsConvertToGoodReceipt && !item.IsVerified && (
                                <Tooltip title="Good Receipt" arrow><Button size="small" onClick={() => handleOpenVerifyModal(item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✅</Button></Tooltip>
                              )}
                              {hasDeleteMasterPermission && !item.IsVerified && <Tooltip title="Delete" arrow><Button size="small" onClick={() => handleOpenDeleteModal(item.Id, item.BillNumber)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button></Tooltip>}
                            </>
                          ) : (
                            <>
                              <Tooltip title="View" arrow><Button size="small" onClick={() => handleOpenInactiveModal(item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button></Tooltip>
                              {hasEditMasterPermission && <Tooltip title="Activate" arrow><Button size="small" onClick={() => handleOpenActivateModal(item.Id, item.BillNumber)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✅</Button></Tooltip>}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Expanded detail row */}
                    {expandedPurchaseRowId === item.Id && (
                      <TableRow key={`${item.Id}-exp`}>
                        <TableCell colSpan={10} style={{ padding: 0, backgroundColor: '#f8fafc' }}>
                          <Collapse in={expandedPurchaseRowId === item.Id} timeout={400}>
                            <Box sx={{ p: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: '12px' }}>
                              <Box><span style={{ color: '#6b7280' }}>Bill No: </span><strong>{item.BillNumber}</strong></Box>
                              <Box><span style={{ color: '#6b7280' }}>Date: </span><strong>{formatDate(item.BillDate)}</strong></Box>
                              {item.PurchaseOrderNo && <Box><span style={{ color: '#6b7280' }}>PO Ref: </span><strong style={{ color: '#7c3aed', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleOpenPODetailsModal({ Id: item.PurchaseOrderId, PurchaseOrderNo: item.PurchaseOrderNo })}>{item.PurchaseOrderNo}</strong></Box>}
                              <Box><span style={{ color: '#6b7280' }}>Taxable: </span><strong>{formatCurrency(item.TaxableAmount || ((parseFloat(item.TotalAmount) || 0) - (parseFloat(item.CGSTAmount) || 0) - (parseFloat(item.SGSTAmount) || 0) - (parseFloat(item.IGSTAmount) || 0)))}</strong></Box>
                              <Box><span style={{ color: '#6b7280' }}>GST: </span><strong>{formatCurrency((item.CGSTAmount || 0) + (item.SGSTAmount || 0) + (item.IGSTAmount || 0))}</strong></Box>
                              <Box><span style={{ color: '#6b7280' }}>Total: </span><strong style={{ color: '#2C3E6E' }}>{formatCurrency(item.TotalAmount)}</strong></Box>
                              {item.PaidAmount > 0 && <Box><span style={{ color: '#6b7280' }}>Paid: </span><strong style={{ color: '#16a34a' }}>{formatCurrency(item.PaidAmount)}</strong></Box>}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '13px' }}>No purchases found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={rowsPerPage}
          onItemsPerPageChange={(v) => { setRowsPerPage(Number(v)); setCurrentPage(1); }}
          totalItems={totalCount}
        />
      </div>



      {/* Purchase Modal */}
      <PurchaseModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        formData={formData}
        setFormData={setFormData}
        purchaseDetails={purchaseDetails}
        setPurchaseDetails={setPurchaseDetails}
        onSave={handleSave}
        onVerify={handleVerifyFromModal}
        onToGoodReceipt={handleOpenConvertModal}
        suppliers={suppliers}
        items={items}
        branches={branches}
        loading={loading}
        freightPackingGST={freightPackingGST}
        companyId={companyId}
        token={token}
        setLoading={setLoading}
        setLoadingMessage={setLoadingMessage}
      />

      {/* Payment Status Popup */}
      <Dialog
        open={Boolean(paymentPopupAnchor)}
        onClose={() => { setPaymentPopupAnchor(null); setPaymentPopupData(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxWidth: '420px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
              Payment Details
            </Typography>
            <IconButton onClick={() => { setPaymentPopupAnchor(null); setPaymentPopupData(null); }} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {paymentPopupData && (() => {
            const paymentIds = paymentPopupData.PaymentId ? String(paymentPopupData.PaymentId).split(',').map(s => s.trim()).filter(s => s && s !== '0') : [];
            const voucherNos = paymentPopupData.PaymentVoucherNo ? String(paymentPopupData.PaymentVoucherNo).split(',').map(s => s.trim()).filter(Boolean) : [];
            const paidAmounts = paymentPopupData.PaymentPaidAmount ? String(paymentPopupData.PaymentPaidAmount).split(',').map(s => parseFloat(s.trim()) || 0) : [];
            
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {paymentIds.length > 0 ? (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                      Payment Vouchers ({paymentIds.length})
                    </Typography>
                    {paymentIds.map((id, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: '#e0f2fe' }, transition: 'background 0.15s' }}
                        onClick={() => handleOpenPaymentModal(id)}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '12px', textDecoration: 'underline', cursor: 'pointer' }}>
                          {voucherNos[idx] || `PMT/${id}`}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669', fontSize: '12px' }}>
                          {paidAmounts[idx] ? `₹${paidAmounts[idx].toLocaleString('en-IN')}` : ''}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f0fdf4', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Payment Voucher No</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 200, color: '#059669', fontSize: '12px' }}>
                      {paymentPopupData.PaymentStatus === 'Paid' ? 'Direct Purchase' : (paymentPopupData.PaymentVoucherNo || '-')}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 300 }}>Payment Mode</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 200, color: '#7c3aed' }}>
                    {paymentPopupData.PaymentMode || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Total Paid</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 200, color: '#059669' }}>
                    {formatCurrency(paymentPopupData.PaymentPaidAmount ? String(paymentPopupData.PaymentPaidAmount).split(',').reduce((s, v) => s + (parseFloat(v) || 0), 0) : 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Pending Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 200, color: '#dc2626' }}>
                    {formatCurrency(paymentPopupData.PaymentPendingAmount || 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>Payment Status</Typography>
                  <Chip
                    label={paymentPopupData.PaymentStatus || '-'}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 200,
                      bgcolor: paymentPopupData.PaymentStatus === 'Paid' ? '#d1fae5' : paymentPopupData.PaymentStatus === 'Partial' ? '#fef3c7' : '#fee2e2',
                      color: paymentPopupData.PaymentStatus === 'Paid' ? '#065f46' : paymentPopupData.PaymentStatus === 'Partial' ? '#92400e' : '#991b1b'
                    }}
                  />
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Payment View Modal */}
      {paymentViewModal.open && !paymentViewLoading && (
        <PaymentReceiptsModal
          open={paymentViewModal.open}
          onClose={() => { setPaymentViewModal({ open: false }); setPaymentViewFormData({}); setPaymentViewDetails([]); }}
          modalAction="view"
          formData={paymentViewFormData}
          setFormData={setPaymentViewFormData}
          paymentReceiptDetails={paymentViewDetails}
          setPaymentReceiptDetails={setPaymentViewDetails}
          onSave={() => {}}
          masters={[{ Id: paymentViewFormData.MasterId, Name: paymentViewFormData.MasterName }]}
          banks={[]}
          purchases={paymentViewFormData.IsType ? paymentViewDetails : []}
          sales={!paymentViewFormData.IsType ? paymentViewDetails : []}
          paymentMethods={[]}
          gstRates={[]}
          loading={paymentViewLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
          fetchPurchases={() => {}}
          fetchSales={() => {}}
          fetchBillsForMaster={() => {}}
          fetchMaxBillNo={() => {}}
          fromDate={fromDate}
          toDate={toDate}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#dc2626' }}>
          <Warning /> Delete Purchase
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete purchase <strong>{deleteItemNo}</strong>?</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog open={activateModalOpen} onClose={handleCloseActivateModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#10b981' }}>
          <CheckCircle /> Activate Purchase
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to activate purchase <strong>{activateItemNo}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivateModal}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">Activate</Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Good Receipt Confirmation Modal */}
      <Dialog open={convertModalOpen} onClose={handleCloseConvertModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#8b5cf6' }}>
          <Assignment /> Convert to Good Receipt
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to convert purchase <strong>{convertItemNo}</strong> to Good Receipt?</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>This action will enable the receipt process for this purchase.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvertModal}>Cancel</Button>
          <Button onClick={handleToGoodReceipt} variant="contained" sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}>Convert</Button>
        </DialogActions>
      </Dialog>

      {/* Inactive Purchase View Modal */}
      <Dialog
        open={inactiveModalOpen}
        onClose={handleCloseInactiveModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh', maxWidth: '1200px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#dc2626', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                View Inactive Purchase
              </Typography>
              <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />
            </Box>
            <IconButton onClick={handleCloseInactiveModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {inactiveViewData && (
            <>
              {/* Purchase Information */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Purchase Information</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Bill Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{inactiveViewData.BillNumber || '-'}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(inactiveViewData.BillDate)}</Typography>
                </Box>
                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{inactiveViewData.MasterName || getSupplierName(inactiveViewData)}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{formatCurrency(inactiveViewData.TotalAmount)}</Typography>
                </Box>
              </Box>

              {/* Purchase Items */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Purchase Items ({inactiveViewDetails.length} items)</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#fee2e2' }}>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#991b1b' }}>Item</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Rcvd Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Dmg Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Stock Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Disc%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Disc Amt</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>GST%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Taxable</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inactiveViewDetails.map((detail, index) => (
                        <tr key={detail.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '8px', fontWeight: 500 }}>{detail.ItemName || items.find(i => i.Id === detail.ItemId)?.Name || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{detail.ReceivedQuantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{detail.DamageQuantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{detail.StockQuantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{detail.DiscountPercentage || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{detail.DiscountAmount || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.GSTRate || 0}%</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.TaxableAmount)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount)}</td>
                        </tr>
                      ))}
                      {inactiveViewDetails.length === 0 && (
                        <tr>
                          <td colSpan="11" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Purchase Summary */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Purchase Summary</Typography>
              <Box sx={{ bgcolor: '#fef2f2', borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Sub Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(inactiveViewData.Amount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Discount:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#dc2626' }}>- {formatCurrency(inactiveViewData.DiscountAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Taxable:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(inactiveViewData.TaxableAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">GST:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#166534' }}>{formatCurrency((inactiveViewData.CGSTAmount || 0) + (inactiveViewData.SGSTAmount || 0) + (inactiveViewData.IGSTAmount || 0))}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626' }}>Grand Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.9rem' }}>{formatCurrency(inactiveViewData.TotalAmount)}</Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#fef2f2' }}>
          <Button onClick={handleCloseInactiveModal} variant="outlined" size="small" sx={{ borderColor: '#dc2626', color: '#dc2626', '&:hover': { borderColor: '#b91c1c', bgcolor: '#fee2e2' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Converted PO List Modal */}
      <Dialog
        open={convertedPOModalOpen}
        onClose={handleCloseConvertedPOModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh', width: '95vw', maxWidth: '1600px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f5f3ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#7c3aed', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Converted Purchase Orders
              </Typography>
              <Chip label={`${convertedPOList.length} POs`} size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#ede9fe', color: '#7c3aed' }} />
            </Box>
            <IconButton onClick={handleCloseConvertedPOModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: '#ede9fe' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>#</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#5b21b6' }}>PO Number</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>PO Date</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#5b21b6' }}>Supplier</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#5b21b6' }}>Total Amount</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>Status</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {convertedPOList.map((po, index) => (
                    <tr key={po.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#faf5ff' }}>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{index + 1}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#7c3aed' }}>{po.PurchaseOrderNo || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{formatDate(po.PurchaseOrderDate)}</td>
                      <td style={{ padding: '10px 12px' }}>{po.MasterName || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(po.TotalAmount)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <Chip
                          label={po.IsConverted ? 'Converted' : 'Pending'}
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: '20px',
                            bgcolor: po.IsConverted ? '#d1fae5' : '#fef3c7',
                            color: po.IsConverted ? '#065f46' : '#92400e'
                          }}
                        />
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="To Purchase">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleOpenPOToPurchaseModal(po)}
                              sx={{
                                fontSize: '0.65rem',
                                py: 0.25,
                                px: 1,
                                minWidth: 'auto',
                                bgcolor: '#10b981',
                                '&:hover': { bgcolor: '#059669' }
                              }}
                            >
                              To Purchase
                            </Button>
                          </Tooltip>
                        </Box>
                      </td>
                    </tr>
                  ))}
                  {convertedPOList.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                        <Assignment sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                        <Typography variant="body2" color="textSecondary">No converted purchase orders found</Typography>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f5f3ff' }}>
          <Button onClick={handleCloseConvertedPOModal} variant="outlined" size="small" sx={{ borderColor: '#7c3aed', color: '#7c3aed', '&:hover': { borderColor: '#6d28d9', bgcolor: '#ede9fe' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* PO to Purchase Create Modal */}
      <Dialog
        open={poToPurchaseModalOpen}
        onClose={handleClosePOToPurchaseModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh', maxWidth: '1600px', width: '95vw' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#10b981', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Create Purchase from PO
              </Typography>
              {selectedPOData && (
                <Chip
                  label={selectedPOData.PurchaseOrderNo}
                  size="small"
                  onClick={() => handleOpenPODetailsModal(selectedPOData)}
                  sx={{
                    fontSize: '0.65rem',
                    height: '20px',
                    bgcolor: '#d1fae5',
                    color: '#065f46',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#a7f3d0' }
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleClosePOToPurchaseModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5 }}>
          {selectedPOData && (
            <>
              {/* Purchase Information Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 0.5, display: 'block' }}>Purchase Information</Typography>
              <Grid container spacing={0.75} sx={{ mb: 1 }} alignItems="flex-end">
                <Grid item xs={6} sm={1.5}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Bill No *</Typography>
                  <input
                    type="text"
                    value={poToPurchaseFormData.BillNumber || ''}
                    onChange={(e) => handlePOToPurchaseFormChange('BillNumber', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: `1px solid ${!poToPurchaseFormData.BillNumber?.trim() ? '#ef4444' : '#d1d5db'}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: !poToPurchaseFormData.BillNumber?.trim() ? '#fef2f2' : 'white'
                    }}
                    placeholder="Enter Bill No"
                    required
                  />
                </Grid>
                <Grid item xs={6} sm={1.3}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.25 }}>Bill Date *</Typography>
                  <input
                    type="date"
                    value={poToPurchaseFormData.BillDate || ''}
                    onChange={(e) => handlePOToPurchaseFormChange('BillDate', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: `1px solid ${!poToPurchaseFormData.BillDate ? '#ef4444' : '#d1d5db'}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: !poToPurchaseFormData.BillDate ? '#fef2f2' : 'white'
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Supplier *</Typography>
                  <input
                    type="text"
                    value={selectedPOData.MasterName || ''}
                    disabled
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', backgroundColor: '#f9fafb', color: '#5D4037', fontWeight: 700 }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>GST No</Typography>
                  <input
                    type="text"
                    value={suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.GSTNumber || suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.GSTNo || '-'}
                    disabled
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', backgroundColor: '#f0fdf4', color: '#5D4037', fontWeight: 700 }}
                  />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Inv Type</Typography>
                  <input
                    type="text"
                    value={suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.InvoiceTypeName || suppliers.find(s => (s.MasterId || s.Id) === selectedPOData.MasterId)?.InvoiceType || '-'}
                    disabled
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', backgroundColor: '#fef3c7', color: '#5D4037', fontWeight: 700 }}
                  />
                </Grid>
                <Grid item xs={6} sm={1.5}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>GST Type *</Typography>
                  <select
                    value={poToPurchaseFormData.GstCalculationType ?? 1}
                    onChange={(e) => {
                      const gstType = Number(e.target.value);

                      const gstTypeName =
                        gstType === 1
                          ? 'Excluding Tax'
                          : gstType === 2
                            ? 'Including Tax'
                            : '';

                      handlePOToPurchaseFormChange('GstCalculationType', gstType);
                      handlePOToPurchaseFormChange('GstCalculationTypeName', gstTypeName);
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: '#e0f2fe',
                    }}
                  >
                    <option value={1}>Excluding Tax</option>
                    <option value={2}>Including Tax</option>
                  </select>
                </Grid>
                <Grid item xs={6} sm={1.2}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Clearing Date</Typography>
                  <input
                    type="date"
                    value={poToPurchaseFormData.ClearingDate || new Date().toISOString().split('T')[0]}
                    onChange={(e) => handlePOToPurchaseFormChange('ClearingDate', e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                  />
                </Grid>
              </Grid>

              {/* Items Table */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981' }}>Items ({selectedPODetails.length} items)</Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => {
                    setPOSelectedItems([]);
                    setPOGetItemsSearch('');
                    setPOGetItemsModalOpen(true);
                  }}
                  sx={{
                    bgcolor: '#10b981',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: '24px',
                    '&:hover': { bgcolor: '#059669' }
                  }}
                >
                  Add Item
                </Button>
              </Box>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#d1fae5' }}>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>#</th>
                        <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600, color: '#065f46' }}>Item</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>HSN</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Order Qty</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Rcvd Qty</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Dis %</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Dis Amt</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Rate</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>GST%</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Taxable</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>GST Amt</th>
                        <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: '#065f46' }}>Total</th>
                        <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 600, color: '#065f46' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPODetails.map((detail, index) => (
                        <tr key={detail.Id || index} style={{
                          borderBottom: '1px solid #e2e8f0',
                          background: index % 2 === 0 ? '#fff' : '#f0fdf4',
                          backgroundColor: (!detail.ItemId || !detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0 || !detail.Rate || parseFloat(detail.Rate) <= 0) ? '#fef2f2' : (index % 2 === 0 ? '#fff' : '#f0fdf4')
                        }}>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '4px 6px', fontWeight: 500, color: 'darkred', fontSize: '0.8rem' }}>
                            {detail.ItemName || '-'}
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center', fontSize: '11px', color: '#6b7280' }}>
                            {detail.HSNCode || '-'}
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <input
                              type="number"
                              value={detail.Quantity || ''}
                              onChange={(e) => handlePOToPurchaseDetailChange(index, 'Quantity', e.target.value)}
                              style={{ width: '60px', padding: '3px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                              min="0"
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <input
                              type="number"
                              value={detail.ReceivedQuantity || ''}
                              onChange={(e) => handlePOToPurchaseDetailChange(index, 'ReceivedQuantity', e.target.value)}
                              onKeyPress={(e) => {
                                // Prevent non-numeric characters except decimal point
                                if (!/[0-9.]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e) => {
                                // Prevent pasting invalid content
                                const paste = (e.clipboardData || window.clipboardData).getData('text');
                                if (!/^[0-9]*\.?[0-9]*$/.test(paste)) {
                                  e.preventDefault();
                                }
                              }}
                              style={{
                                width: '60px',
                                padding: '3px',
                                border: `1px solid ${!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0 ? '#ef4444' : '#d1d5db'}`,
                                borderRadius: '4px',
                                textAlign: 'center',
                                fontSize: '12px',
                                backgroundColor: (!detail.ReceivedQuantity || parseFloat(detail.ReceivedQuantity) <= 0) ? '#fef2f2' : 'white'
                              }}
                              min="0"
                              step="0.01"
                              required
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <input
                              type="number"
                              value={detail.DiscountPercentage || 0}
                              onChange={(e) => handlePOToPurchaseDetailChange(index, 'DiscountPercentage', e.target.value)}
                              style={{ width: '60px', padding: '3px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                              min="0"
                              max="100"
                              step="0.01"
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                            <input
                              type="number"
                              value={detail.DiscountAmount || 0}
                              onChange={(e) => handlePOToPurchaseDetailChange(index, 'DiscountAmount', e.target.value)}
                              style={{ width: '80px', padding: '3px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'right', fontSize: '12px' }}
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                            <input
                              type="number"
                              value={detail.Rate || ''}
                              onChange={(e) => handlePOToPurchaseDetailChange(index, 'Rate', e.target.value)}
                              onKeyPress={(e) => {
                                // Prevent non-numeric characters except decimal point
                                if (!/[0-9.]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e) => {
                                // Prevent pasting invalid content
                                const paste = (e.clipboardData || window.clipboardData).getData('text');
                                if (!/^[0-9]*\.?[0-9]*$/.test(paste)) {
                                  e.preventDefault();
                                }
                              }}
                              style={{
                                width: '80px',
                                padding: '3px',
                                border: `1px solid ${!detail.Rate || parseFloat(detail.Rate) <= 0 ? '#ef4444' : '#d1d5db'}`,
                                borderRadius: '4px',
                                textAlign: 'right',
                                fontSize: '12px',
                                backgroundColor: (!detail.Rate || parseFloat(detail.Rate) <= 0) ? '#fef2f2' : 'white'
                              }}
                              min="0"
                              step="0.01"
                              required
                            />
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#059669' }}>{detail.GSTRate || 0}%</span>
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(detail.TaxableAmount || 0)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>{formatCurrency((detail.CGSTAmount || 0) + (detail.SGSTAmount || 0) + (detail.IGSTAmount || 0))}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(detail.TotalAmount || 0)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updatedDetails = selectedPODetails.filter((_, i) => i !== index);
                                setSelectedPODetails(updatedDetails);
                                calculatePOTotals(updatedDetails);
                              }}
                              sx={{ color: '#ef4444', p: 0.5 }}
                            >
                              <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                      {selectedPODetails.length === 0 && (
                        <tr>
                          <td colSpan="13" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Additional Charges & Summary Layout */}
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {/* Additional Charges Section */}
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 0.5, display: 'block' }}>Additional Charges</Typography>
                  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 1, bgcolor: '#f9fafb' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Freight</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.FreightAmount || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('FreightAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Freight GST %</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.FreightGSTRate ?? ''}
                          onChange={(e) => handlePOToPurchaseFormChange('FreightGSTRate', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Packing</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.PackingAmount || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('PackingAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Packing GST %</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.PackingGSTRate ?? ''}
                          onChange={(e) => handlePOToPurchaseFormChange('PackingGSTRate', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>TCS Amount</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.TCSAmount || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('TCSAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Paid Amount</Typography>
                        <input
                          type="number"
                          value={poToPurchaseFormData.PaidAmount || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('PaidAmount', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', textAlign: 'right' }}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Remarks</Typography>
                        <textarea
                          value={poToPurchaseFormData.AdditionalRemarks || ''}
                          onChange={(e) => handlePOToPurchaseFormChange('AdditionalRemarks', e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', minHeight: '60px', resize: 'vertical' }}
                          placeholder="Enter remarks..."
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Summary Section */}
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981', mb: 0.5, display: 'block' }}>Summary</Typography>
                  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 1, bgcolor: '#f0fdf4' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Subtotal</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.Amount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Discount</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#dc2626', fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.DiscountAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Taxable</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.TaxableAmount || 0)}</Typography>
                    </Box>
                    {/* Show IGST or CGST/SGST based on invoice type */}
                    {(() => {
                      const poInvoiceType = selectedPOData?.InvoiceTypeName?.toLowerCase() || '';
                      const formInvoiceType = poToPurchaseFormData?.InvoiceTypeName?.toLowerCase() || '';
                      const hasIGSTAmount = (poToPurchaseFormData.IGSTAmount || 0) > 0;
                      const hasNoCGSTSGST = (poToPurchaseFormData.CGSTAmount || 0) === 0 && (poToPurchaseFormData.SGSTAmount || 0) === 0;
                      const itemsHaveIGST = selectedPODetails.some(item => (item.IGSTAmount || 0) > 0);
                      const isIGST = poInvoiceType.includes('igst') || formInvoiceType.includes('igst') || (hasIGSTAmount && hasNoCGSTSGST) || (hasIGSTAmount > 0) || itemsHaveIGST;

                      return isIGST ? (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>IGST</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.IGSTAmount || 0)}</Typography>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>CGST</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.CGSTAmount || 0)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>SGST</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.SGSTAmount || 0)}</Typography>
                          </Box>
                        </>
                      );
                    })()}
                    {(parseFloat(poToPurchaseFormData.FreightAmount) || 0) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Freight</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.FreightAmount || 0)}</Typography>
                      </Box>
                    )}
                    {(parseFloat(poToPurchaseFormData.FreightAmount) || 0) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Freight GST ({Math.round(poToPurchaseFormData.FreightGSTRate || 0)}%)</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.FreightGSTAmount || 0)}</Typography>
                      </Box>
                    )}
                    {(parseFloat(poToPurchaseFormData.PackingAmount) || 0) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Packing</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.PackingAmount || 0)}</Typography>
                      </Box>
                    )}
                    {(parseFloat(poToPurchaseFormData.PackingAmount) || 0) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Packing GST ({Math.round(poToPurchaseFormData.PackingGSTRate || 0)}%)</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.PackingGSTAmount || 0)}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>TCS</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.TCSAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Round Off</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{(poToPurchaseFormData.RoundOff || 0) >= 0 ? '+' : ''}{(poToPurchaseFormData.RoundOff || 0).toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.8rem' }}>Total</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.85rem' }}>{formatCurrency(poToPurchaseFormData.TotalAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Paid</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#10b981', fontSize: '0.75rem' }}>{formatCurrency(poToPurchaseFormData.PaidAmount || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2, borderTop: '1px solid #d1fae5', pt: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>Balance</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.85rem' }}>{formatCurrency((poToPurchaseFormData.TotalAmount || 0) - (poToPurchaseFormData.PaidAmount || 0))}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
          <Button onClick={handleClosePOToPurchaseModal} variant="outlined" size="small" sx={{ borderColor: '#10b981', color: '#10b981', '&:hover': { borderColor: '#059669', bgcolor: '#d1fae5' } }}>
            Cancel
          </Button>
          <Button onClick={handleSavePOToPurchase} variant="contained" size="small" disabled={loading} sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
            Create Purchase
          </Button>
        </DialogActions>
      </Dialog>

      {/* PO Get Items Modal */}
      <Dialog open={poGetItemsModalOpen} onClose={() => setPOGetItemsModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}>
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0fdf4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#065f46' }}>
            Get Items ({items.filter(i => {
              const q = poGetItemsSearch.toLowerCase();
              return !q || (i.Name || '').toLowerCase().includes(q) || (i.HSNCode || '').toLowerCase().includes(q);
            }).length} available)
          </Typography>
          <IconButton onClick={() => setPOGetItemsModalOpen(false)} size="small"><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Search */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0' }}>
            <input
              autoFocus
              placeholder="Search item name or HSN..."
              value={poGetItemsSearch}
              onChange={e => setPOGetItemsSearch(e.target.value)}
              style={{ width: '100%', height: 32, padding: '0 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
            />
          </Box>
          {/* Items list */}
          <Box sx={{ overflow: 'auto', maxHeight: 'calc(85vh - 180px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#d1fae5', zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '6px 10px', textAlign: 'center', width: 40, color: '#065f46', fontWeight: 700 }}>✓</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: '#065f46', fontWeight: 700 }}>Item Name</th>
                  <th style={{ padding: '6px 10px', textAlign: 'center', color: '#065f46', fontWeight: 700 }}>HSN</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right', color: '#065f46', fontWeight: 700 }}>Rate</th>
                  <th style={{ padding: '6px 10px', textAlign: 'center', color: '#065f46', fontWeight: 700 }}>Qty</th>
                </tr>
              </thead>
              <tbody>
                {items
                  .filter(i => {
                    const q = poGetItemsSearch.toLowerCase();
                    return !q || (i.Name || '').toLowerCase().includes(q) || (i.HSNCode || '').toLowerCase().includes(q);
                  })
                  .map((item, idx) => {
                    const sel = poSelectedItems.find(s => s.Id === item.Id);
                    const isSelected = !!sel;
                    const alreadyAdded = selectedPODetails.some(d => d.ItemId === item.Id);
                    return (
                      <tr key={item.Id} style={{ background: isSelected ? '#ecfdf5' : idx % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f1f5f9', opacity: alreadyAdded ? 0.5 : 1 }}>
                        <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                          <input type="checkbox" checked={isSelected} disabled={alreadyAdded}
                            onChange={e => {
                              if (e.target.checked) {
                                setPOSelectedItems(prev => [...prev, { ...item, qty: 1, rate: item.PurchaseRate || item.SaleRate || 0 }]);
                              } else {
                                setPOSelectedItems(prev => prev.filter(s => s.Id !== item.Id));
                              }
                            }} />
                        </td>
                        <td style={{ padding: '5px 10px', fontWeight: 600, color: '#1e293b' }}>{item.HSNCode ? `${item.HSNCode} - ${item.Name}` : item.Name}</td>
                        <td style={{ padding: '5px 10px', textAlign: 'center', color: '#64748b' }}>{item.HSNCode || '-'}</td>
                        <td style={{ padding: '5px 10px', textAlign: 'right', color: '#374151' }}>₹{(item.PurchaseRate || item.SaleRate || 0).toFixed(2)}</td>
                        <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                          {isSelected && (
                            <input type="number" value={sel.qty} min={1}
                              onChange={e => setPOSelectedItems(prev => prev.map(s => s.Id === item.Id ? { ...s, qty: parseFloat(e.target.value) || 1 } : s))}
                              style={{ width: 60, height: 26, textAlign: 'center', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1, borderTop: '1px solid #e2e8f0', bgcolor: '#f0fdf4', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#059669' }}>{poSelectedItems.length} item(s) selected</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setPOGetItemsModalOpen(false)} variant="outlined" size="small" sx={{ borderColor: '#10b981', color: '#10b981' }}>Cancel</Button>
            <Button
              variant="contained" size="small" disabled={poSelectedItems.length === 0}
              sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
              onClick={() => {
                const isIGST = (poToPurchaseFormData.GstCalculationTypeName || '').toLowerCase().includes('igst') ||
                  (selectedPOData?.InvoiceTypeName || '').toLowerCase().includes('igst');
                const isIncludingTax = (poToPurchaseFormData.GstCalculationTypeName || '') === 'Including Tax';

                const newItems = poSelectedItems.map(sel => {
                  const qty = parseFloat(sel.qty) || 1;
                  const rate = parseFloat(sel.rate || sel.PurchaseRate || sel.SaleRate || 0);
                  const gstRate = parseFloat(sel.GSTRate || 0);
                  let baseAmount, taxable, gstAmt;
                  if (isIncludingTax) {
                    baseAmount = (qty * rate) / (1 + gstRate / 100);
                  } else {
                    baseAmount = qty * rate;
                  }
                  taxable = Math.round(baseAmount * 100) / 100;
                  gstAmt = Math.round((taxable * gstRate / 100) * 100) / 100;
                  return {
                    Id: 0,
                    ItemId: sel.Id,
                    ItemName: sel.Name,
                    HSNCode: sel.HSNCode || '',
                    Quantity: qty,
                    ReceivedQuantity: qty,
                    BilledQuantity: qty,
                    Rate: rate,
                    Amount: Math.round(qty * rate * 100) / 100,
                    DiscountPercentage: 0,
                    DiscountAmount: 0,
                    GSTRate: gstRate,
                    TaxableAmount: taxable,
                    CGSTAmount: isIGST ? 0 : Math.round((gstAmt / 2) * 100) / 100,
                    SGSTAmount: isIGST ? 0 : Math.round((gstAmt / 2) * 100) / 100,
                    IGSTAmount: isIGST ? gstAmt : 0,
                    TotalAmount: Math.round((taxable + gstAmt) * 100) / 100,
                    DamageQuantity: 0,
                    StockQuantity: qty,
                  };
                });

                const updatedDetails = [...selectedPODetails, ...newItems];
                setSelectedPODetails(updatedDetails);
                calculatePOTotals(updatedDetails);
                setPOGetItemsModalOpen(false);
                setPOSelectedItems([]);
              }}
            >
              Add Selected ({poSelectedItems.length})
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* PO Details View Modal */}
      <Dialog
        open={poDetailsModalOpen}
        onClose={handleClosePODetailsModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh', maxWidth: '1000px' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#0ea5e9', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Purchase Order Details
              </Typography>
              {poDetailsData && (
                <Chip label={poDetailsData.PurchaseOrderNo} size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#dbeafe', color: '#1e40af' }} />
              )}
            </Box>
            <IconButton onClick={handleClosePODetailsModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {poDetailsData && (
            <>
              {/* PO Header Information */}
              <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1, mb: 2, border: '1px solid #dbeafe' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>PO Number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af' }}>{poDetailsData.PurchaseOrderNo || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>PO Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(poDetailsData.PurchaseOrderDate)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>{poDetailsData.MasterName || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>{formatCurrency(poDetailsData.TotalAmount)}</Typography>
                  </Grid>

                  {/* Second Row */}
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Remarks</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {poDetailsData.Remarks || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Instruction 1</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {poDetailsData.Instruction1 || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Instruction 2</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      {poDetailsData.Instruction2 || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* PO Items Table */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#0ea5e9', mb: 1, display: 'block' }}>
                PO Items ({poDetailsItems.length} items)
              </Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#dbeafe' }}>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#1e40af' }}>Item Name</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>Quantity</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Amount</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>GST%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>GST Amount</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poDetailsItems.map((item, index) => (
                        <tr key={item.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '8px', fontWeight: 500 }}>{item.ItemName || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{item.Quantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.Rate || 0)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(item.Amount || 0)}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#dc2626' }}>{item.GSTRate || 0}%</span>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>
                            {formatCurrency((item.CGSTAmount || 0) + (item.SGSTAmount || 0) + (item.IGSTAmount || 0))}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(item.TotalAmount || 0)}</td>
                        </tr>
                      ))}
                      {poDetailsItems.length === 0 && (
                        <tr>
                          <td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* PO Summary */}
              <Box sx={{ p: 1, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #dbeafe' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#0ea5e9', mb: 0.5, display: 'block' }}>PO Summary</Typography>
                <Grid container spacing={1}>
                  {/* First Row */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Subtotal</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.Amount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Discount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.DiscountAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Taxable</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.TaxableAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency((poDetailsData.CGSTAmount || 0) + (poDetailsData.SGSTAmount || 0) + (poDetailsData.IGSTAmount || 0))}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Freight</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.FreightAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Second Row */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Freight GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.FreightGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Packing</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.PackingAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Packing GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.PackingGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>TCS</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.TCSAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#dbeafe', borderRadius: 1, border: '1px solid #93c5fd' }}>
                      <Typography variant="caption" sx={{ color: '#1e40af', display: 'block', mb: 0.2, fontWeight: 600, fontSize: '0.7rem' }}>Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e40af', fontSize: '0.85rem' }}>
                        {formatCurrency(poDetailsData.TotalAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Third Row - Additional Details */}
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>CGST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.CGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>SGST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.SGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>IGST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.IGSTAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Round Off</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {(poDetailsData.RoundOff || 0) >= 0 ? '+' : ''}{(poDetailsData.RoundOff || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Paid Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.8rem' }}>
                        {formatCurrency(poDetailsData.PaidAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Button onClick={handleClosePODetailsModal} variant="contained" size="small" sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Drill-down Modal */}
      <Dialog open={supplierModalOpen} onClose={handleCloseSupplierModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Purchases - {selectedSupplier?.name}</Typography>
          <IconButton onClick={handleCloseSupplierModal}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {supplierPurchases.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>No purchases found for this supplier</Typography>
          ) : (
            supplierPurchases.map((purchase) => (
              <Box key={purchase.Id} sx={{ mb: 2, border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => togglePurchaseExpand(purchase.Id)}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e40af' }}>{purchase.BillNumber}</Typography>
                    <Typography variant="caption" color="textSecondary">{formatDate(purchase.BillDate)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(purchase.TotalAmount)}</Typography>
                    {expandedPurchaseId === purchase.Id ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                </Box>
                <Collapse in={expandedPurchaseId === purchase.Id}>
                  <Box sx={{ p: 2, bgcolor: '#fff' }}>
                    {(purchase.PurchaseDetails || []).map((d, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: idx < (purchase.PurchaseDetails?.length || 0) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <Typography variant="body2">{d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || 'Item'}</Typography>
                        <Typography variant="body2" color="textSecondary">{d.ReceivedQuantity} × {formatCurrency(d.Rate)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>

      {/* Good Receipt Modal */}
      {verifyModalOpen && (
        <VerifyPurchaseModal
          open={verifyModalOpen}
          onClose={handleCloseVerifyModal}
          formData={verifyPurchaseData}
          purchaseDetails={verifyPurchaseDetails}
          onVerify={handleUpdateFromGoodReceipt}
          onActualVerify={handleActualVerify}
          suppliers={suppliers}
          items={items}
          branches={branches}
          loading={loading}
        />
      )}

      {/* Purchase Return Modal */}
      <Dialog
        open={purchaseReturnModalOpen}
        onClose={handleClosePurchaseReturnModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
            maxWidth: { xs: '95vw', sm: '90vw', md: '1400px' },
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fef3c7' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#f59e0b', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Create Purchase Return
              </Typography>
              {purchaseReturnData && (
                <Chip
                  label={`From: ${purchaseReturnData.PurchaseNo || purchaseReturnData.BillNumber || 'N/A'}`}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: '20px',
                    bgcolor: '#dbeafe',
                    color: '#1e40af',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#bfdbfe'
                    }
                  }}
                  onClick={() => handleOpenPurchaseDetailsViewModal(purchaseReturnData)}
                />
              )}
            </Box>
            <IconButton onClick={handleClosePurchaseReturnModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {purchaseReturnData && (
            <>
              {/* Return Information */}
              <Box sx={{
                p: 1,
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: 1.5,
                mb: 2,
                border: '1px solid #f59e0b',
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
                position: 'relative',
                zIndex: 2
              }}>
                <Grid container spacing={1}>
                  {/* Input Fields Row */}
                  <Grid item xs={6} sm={2.5}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      R.No *
                    </Typography>
                    <input
                      type="text"
                      value={purchaseReturnFormData.ReturnNumber || ''}
                      onChange={(e) => handlePurchaseReturnFormChange('ReturnNumber', e.target.value)}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#f9fafb',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        cursor: 'not-allowed',
                        color: '#6b7280',
                        fontFamily: 'inherit'
                      }}
                      placeholder="Auto-generated"
                    />
                  </Grid>

                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      R.Date *
                    </Typography>
                    <input
                      type="date"
                      value={purchaseReturnFormData.ReturnDate || ''}
                      onChange={(e) => handlePurchaseReturnFormChange('ReturnDate', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f59e0b';
                        e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      GST Type *
                    </Typography>
                    <select
                      value={purchaseReturnFormData.GstCalculationType || 1}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        handlePurchaseReturnFormChange('GstCalculationType', value);
                        handlePurchaseReturnFormChange('GstCalculationTypeName', value === 1 ? 'Excluding Tax' : 'Including Tax');
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        fontFamily: 'inherit',
                        cursor: 'pointer'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f59e0b';
                        e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value={1}>Excluding Tax</option>
                      <option value={2}>Including Tax</option>
                    </select>
                  </Grid>

                  <Grid item xs={12} sm={5.5}>
                    <Typography variant="caption" sx={{
                      color: '#374151',
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      Remarks
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={purchaseReturnFormData.Remarks || ''}
                      onChange={(e) => handlePurchaseReturnFormChange('Remarks', e.target.value)}
                      placeholder="Enter return remarks..."
                      variant="outlined"
                      InputProps={{
                        sx: {
                          fontSize: '13px',
                          fontWeight: '500',
                          backgroundColor: '#ffffff',
                          borderRadius: '6px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d1d5db'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#f59e0b'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#f59e0b',
                            boxShadow: '0 0 0 3px rgba(245, 158, 11, 0.1)'
                          }
                        }
                      }}
                      inputProps={{
                        style: { padding: '6px 8px' }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Return Items Table */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Return Items</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                {purchaseReturnDetails.length > 0 ? (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.8rem'
                    }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ background: '#fed7aa' }}>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '35px', fontSize: '0.75rem' }}>#</th>
                          <th style={{ padding: '8px 6px', textAlign: 'left', fontWeight: 600, color: '#ea580c', minWidth: '120px', fontSize: '0.75rem' }}>Item</th>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '70px', fontSize: '0.75rem' }}>HSN</th>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '75px', fontSize: '0.75rem' }}>Purchased</th>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '85px', fontSize: '0.75rem' }}>Return Qty *</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '75px', fontSize: '0.75rem' }}>Rate</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '80px', fontSize: '0.75rem' }}>Amount</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '70px', fontSize: '0.75rem' }}>Disc %</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '80px', fontSize: '0.75rem' }}>Disc Amt</th>
                          <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#ea580c', minWidth: '60px', fontSize: '0.75rem' }}>GST%</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '80px', fontSize: '0.75rem' }}>Taxable</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '90px', fontSize: '0.75rem' }}>GST Amount</th>
                          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#ea580c', minWidth: '85px', fontSize: '0.75rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseReturnDetails.map((detail, index) => (
                          <tr key={detail.Id || index} style={{
                            borderBottom: '1px solid #e2e8f0',
                            background: index % 2 === 0 ? '#fff' : '#fefbf3'
                          }}>
                            <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: '0.75rem' }}>{index + 1}</td>
                            <td style={{ padding: '8px 6px', fontWeight: 500, fontSize: '0.75rem' }}>
                              {detail.ItemName || items.find(i => i.Id === detail.ItemId)?.Name || '-'}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: '0.7rem', color: '#6b7280' }}>
                              {detail.HSNCode || '-'}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem' }}>
                              {detail.PreviousStockQuantity > 0 ? detail.PreviousStockQuantity : (detail.PurchaseQuantity || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <input
                                type="number"
                                value={detail.ReturnQuantity || ''}
                                onChange={(e) => handlePurchaseReturnDetailChange(index, 'ReturnQuantity', e.target.value)}
                                style={{
                                  width: '70px',
                                  padding: '4px',
                                  border: `1px solid ${(detail.ReturnQuantity || 0) > (detail.MaxReturnQuantity || 0) ? '#ef4444' : '#d1d5db'}`,
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  fontSize: '0.75rem',
                                  backgroundColor: (detail.ReturnQuantity || 0) > (detail.MaxReturnQuantity || 0) ? '#fef2f2' : 'white'
                                }}
                                min="0"
                                max={detail.MaxReturnQuantity || 0}
                                step="0.01"
                                placeholder="0"
                              />
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem' }}>
                              {formatCurrency(detail.Rate || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: (detail.ReturnQuantity || 0) > 0 ? '#374151' : '#9ca3af', fontSize: '0.75rem' }}>
                              {formatCurrency(detail.ReturnAmount || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                              <input
                                type="number"
                                value={detail.DiscountPercentage || ''}
                                onChange={(e) => handlePurchaseReturnDetailChange(index, 'DiscountPercentage', e.target.value)}
                                style={{
                                  width: '60px',
                                  padding: '4px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  textAlign: 'right',
                                  fontSize: '0.75rem',
                                  backgroundColor: 'white'
                                }}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                                title="Discount percentage (max 100%)"
                              />
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                              <input
                                type="number"
                                value={detail.ReturnDiscountAmount || ''}
                                onChange={(e) => handlePurchaseReturnDetailChange(index, 'DiscountAmount', e.target.value)}
                                style={{
                                  width: '70px',
                                  padding: '4px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  textAlign: 'right',
                                  fontSize: '0.75rem',
                                  backgroundColor: 'white'
                                }}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                title={`Discount amount (max ₹${getMaxDiscountAmountForReturn(detail.ReturnQuantity, detail.Rate, detail.GSTRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of ${isIncludingTaxForReturn() ? 'base' : 'item'} amount)`}
                              />
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <span style={{ fontWeight: 600, color: '#059669', fontSize: '0.75rem' }}>{detail.GSTRate || 0}%</span>
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: (detail.ReturnQuantity || 0) > 0 ? '#059669' : '#9ca3af', fontSize: '0.75rem' }}>
                              {formatCurrency(detail.ReturnTaxableAmount || 0)}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: (detail.ReturnQuantity || 0) > 0 ? '#dc2626' : '#9ca3af', fontSize: '0.75rem' }}>
                              {formatCurrency((detail.ReturnCGSTAmount || 0) + (detail.ReturnSGSTAmount || 0) + (detail.ReturnIGSTAmount || 0))}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: (detail.ReturnQuantity || 0) > 0 ? '#10b981' : '#9ca3af', fontSize: '0.75rem' }}>
                              {formatCurrency(detail.ReturnTotalAmount || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', color: '#6b7280' }}>
                    <Typography variant="body2">
                      {purchaseReturnData ?
                        'No items found in this purchase. Please check the purchase details.' :
                        'Loading purchase items...'
                      }
                    </Typography>
                    {purchaseReturnData && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9ca3af' }}>
                        Purchase ID: {purchaseReturnData.Id} | Bill Number: {purchaseReturnData.BillNumber}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Return Summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Return Summary</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                <Grid container>
                  {/* Left Side - Editable Amount Fields */}
                  <Grid item xs={12} md={4} sx={{ p: 1, borderRight: { md: '1px solid #e2e8f0' } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Additional Charges</Typography>

                    {/* First Row - Freight and Packing */}
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{
                          color: '#6b7280',
                          display: 'block',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Freight Amount
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: '#059669',
                          display: 'block',
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          mb: 0.5
                        }}>
                          GST Rate: {purchaseReturnFormData.FreightGSTRate || 18}%
                        </Typography>
                        <input
                          type="number"
                          value={purchaseReturnFormData.FreightAmount || ''}
                          onChange={(e) => handlePurchaseReturnFormChange('FreightAmount', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'white',
                            outline: 'none',
                            textAlign: 'right'
                          }}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f59e0b';
                            e.target.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{
                          color: '#6b7280',
                          display: 'block',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Packing Amount
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: '#059669',
                          display: 'block',
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          mb: 0.5
                        }}>
                          GST Rate: {purchaseReturnFormData.PackingGSTRate || 18}%
                        </Typography>
                        <input
                          type="number"
                          value={purchaseReturnFormData.PackingAmount || ''}
                          onChange={(e) => handlePurchaseReturnFormChange('PackingAmount', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'white',
                            outline: 'none',
                            textAlign: 'right'
                          }}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f59e0b';
                            e.target.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* Second Row - TCS and Received Amount */}
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{
                          color: '#6b7280',
                          display: 'block',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          TCS Amount
                        </Typography>
                        <input
                          type="number"
                          value={purchaseReturnFormData.TCSAmount || ''}
                          onChange={(e) => handlePurchaseReturnFormChange('TCSAmount', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'white',
                            outline: 'none',
                            textAlign: 'right'
                          }}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f59e0b';
                            e.target.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{
                          color: '#6b7280',
                          display: 'block',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Paid Amount
                        </Typography>
                        <input
                          type="number"
                          value={purchaseReturnFormData.PaidAmount || ''}
                          onChange={(e) => handlePurchaseReturnFormChange('PaidAmount', parseFloat(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'white',
                            outline: 'none',
                            textAlign: 'right'
                          }}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f59e0b';
                            e.target.style.boxShadow = '0 0 0 2px rgba(245, 158, 11, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Right Side - Calculated Summary */}
                  <Grid item xs={12} md={8} sx={{ p: 1, bgcolor: '#fefbf3' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Return Calculation</Typography>

                    {/* Calculation Cards Layout */}
                    <Grid container spacing={0.8}>
                      {/* Subtotal */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Subtotal
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            ₹{(purchaseReturnFormData.Amount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Discount */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Discount
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                            ₹{(purchaseReturnFormData.DiscountAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Taxable */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Taxable
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            ₹{(purchaseReturnFormData.TaxableAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* GST Total */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            GST Total
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                            ₹{((purchaseReturnFormData.CGSTAmount || 0) + (purchaseReturnFormData.SGSTAmount || 0) + (purchaseReturnFormData.IGSTAmount || 0)).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Freight */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Freight
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            ₹{(purchaseReturnFormData.FreightAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Freight GST */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Freight GST
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                            ₹{(purchaseReturnFormData.FreightGSTAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Packing */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Packing
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            ₹{(purchaseReturnFormData.PackingAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Packing GST */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Packing GST
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                            ₹{(purchaseReturnFormData.PackingGSTAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* TCS */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            TCS
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            ₹{(purchaseReturnFormData.TCSAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Round Off */}
                      <Grid item xs={6} md={2.4}>
                        <Box sx={{
                          p: 0.8,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.3, fontSize: '0.65rem', fontWeight: 600 }}>
                            Round Off
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                            {(purchaseReturnFormData.RoundOff || 0) >= 0 ? '+' : ''}₹{(purchaseReturnFormData.RoundOff || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Total Amount and Balance Amount - Side by Side */}
                    <Grid container spacing={0.8} sx={{ mt: 1 }}>
                      <Grid item xs={6} sm={6}>
                        <Box sx={{ p: 1, bgcolor: '#fed7aa', borderRadius: 1, border: '2px solid #f59e0b', textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#ea580c', display: 'block', mb: 0.1, fontSize: '0.75rem', fontWeight: 600 }}>Return Total Amount</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ea580c', fontSize: '1.1rem' }}>
                            ₹{(purchaseReturnFormData.TotalAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={6} sm={6}>
                        <Box sx={{ p: 1, bgcolor: '#e0f2fe', borderRadius: 1, border: '1px solid #0ea5e9', textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#0369a1', display: 'block', mb: 0.1, fontSize: '0.75rem', fontWeight: 600 }}>Balance Amount</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0369a1', fontSize: '1rem' }}>
                            ₹{((purchaseReturnFormData.TotalAmount || 0) - (purchaseReturnFormData.PaidAmount || 0)).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#fef3c7' }}>
          <Button onClick={handleClosePurchaseReturnModal} variant="outlined" size="small" sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fed7aa' } }}>
            Cancel
          </Button>
          <Button onClick={handleSavePurchaseReturn} variant="contained" size="small" disabled={loading} sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
            Create Return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase Details View Modal (for bill number click) */}
      <Dialog
        open={purchaseDetailsViewModalOpen}
        onClose={handleClosePurchaseDetailsViewModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility sx={{ color: '#0ea5e9', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Purchase Details
              </Typography>
              {purchaseDetailsViewData && (
                <Chip label={purchaseDetailsViewData.BillNumber} size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#dbeafe', color: '#1e40af' }} />
              )}
            </Box>
            <IconButton onClick={handleClosePurchaseDetailsViewModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {purchaseDetailsViewData && (
            <>
              {/* Purchase Information */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                    {purchaseDetailsViewData.MasterName || getSupplierName(purchaseDetailsViewData)}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(purchaseDetailsViewData.BillDate)}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{formatCurrency(purchaseDetailsViewData.TotalAmount)}</Typography>
                </Box>
                {purchaseDetailsViewData.PurchaseOrderNo && (
                  <Box sx={{ minWidth: 120 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>PO Number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{purchaseDetailsViewData.PurchaseOrderNo}</Typography>
                  </Box>
                )}
              </Box>

              {/* Purchase Items */}
              {purchaseDetailsViewItems.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Items</Typography>
                  <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ background: '#dbeafe' }}>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>#</th>
                            <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#1e40af' }}>Item</th>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>Qty</th>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>Damage Qty</th>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>Stock Qty</th>
                            <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Rate</th>
                            <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#1e40af' }}>GST%</th>
                            <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchaseDetailsViewItems.map((item, index) => (
                            <tr key={item.Id || index} style={{
                              borderBottom: '1px solid #e2e8f0',
                              background: index % 2 === 0 ? '#fff' : '#f8fafc'
                            }}>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                              <td style={{ padding: '8px', fontWeight: 500 }}>
                                {item.ItemName || items.find(i => i.Id === item.ItemId)?.Name || '-'}
                                {item.HSNCode && (
                                  <div style={{ fontSize: '11px', color: '#6b7280' }}>HSN: {item.HSNCode}</div>
                                )}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>
                                {item.ReceivedQuantity || item.Quantity || 0}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#dc2626' }}>
                                {item.DamageQuantity || 0}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#059669' }}>
                                {item.StockQuantity || 0}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                                {formatCurrency(item.Rate || 0)}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>
                                <span style={{ fontWeight: 600, color: '#059669' }}>{item.GSTRate || 0}%</span>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                                {formatCurrency(item.TotalAmount || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                </>
              )}

              {/* Purchase Summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Summary</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 2, bgcolor: '#f8fafc' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Subtotal</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency(purchaseDetailsViewData.Amount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>GST</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.8rem' }}>
                        {formatCurrency((purchaseDetailsViewData.CGSTAmount || 0) + (purchaseDetailsViewData.SGSTAmount || 0) + (purchaseDetailsViewData.IGSTAmount || 0))}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Other Charges</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                        {formatCurrency((purchaseDetailsViewData.FreightAmount || 0) + (purchaseDetailsViewData.PackingAmount || 0) + (purchaseDetailsViewData.TCSAmount || 0))}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.2, fontSize: '0.7rem' }}>Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af', fontSize: '0.9rem' }}>
                        {formatCurrency(purchaseDetailsViewData.TotalAmount || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f0f9ff' }}>
          <Button onClick={handleClosePurchaseDetailsViewModal} variant="outlined" size="small" sx={{ borderColor: '#0ea5e9', color: '#0ea5e9', '&:hover': { borderColor: '#0284c7', bgcolor: '#dbeafe' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Loading Spinner */}
      {modalLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #e5e7eb'
            }}
          >
            {/* Modern Spinning Icon */}
            <Box
              sx={{
                width: 48,
                height: 48,
                border: '4px solid #f3f4f6',
                borderTop: '4px solid #f59e0b',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />

            {/* Loading Text */}
            <Typography
              variant="body1"
              sx={{
                color: '#374151',
                fontWeight: 500,
                fontSize: '0.95rem',
                textAlign: 'center'
              }}
            >
              Loading purchase details...
            </Typography>

            {/* Animated Dots */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    backgroundColor: '#f59e0b',
                    borderRadius: '50%',
                    animation: `bounce 1.4s ease-in-out ${index * 0.16}s infinite both`,
                    '@keyframes bounce': {
                      '0%, 80%, 100%': {
                        transform: 'scale(0)',
                        opacity: 0.5
                      },
                      '40%': {
                        transform: 'scale(1)',
                        opacity: 1
                      }
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default PurchaseIndex;

